import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import { ExportService } from '../lib/export-utils';
import GTMService from '../lib/gtm';
import { 
  Upload,
  FileImage,
  FileVideo,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Download,
  Share2,
  Eye,
  GitBranch,
  Clock,
  Globe,
  Zap,
  Target,
  TrendingUp,
  Users,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  Maximize,
  RotateCcw,
  Copy,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  Calendar,
  User,
  CreditCard,
  ArrowLeft,
  Info,
  AlertCircle,
  Shield,
  BookOpen,
  Lightbulb,
  X,
  Star,
  Award,
  Brain,
  Activity,
  BarChart3,
  Gauge,
  Flame,
  Heart,
  MousePointer,
  Sparkles,
  Edit2,
  Check,
  Loader2,
  Palette,
  Layout,
  Type,
  Archive,
  FileText,
  Code
} from 'lucide-react';
import { RecentAnalysesWidget } from '../components/widgets/RecentAnalysesWidget';
import { CreativeService, CreativeProgress } from '../lib/creatives';
import { ProjectService, ProjectWithMembers } from '../lib/projects';
import { PlatformAnalysis, AIAnalysisService, AnalysisResult } from '../lib/ai-analysis';
import { RecentAnalysesService } from '../lib/recent-analyses';
import { VersionService, CreateVersionRequest, VersionComparison } from '../lib/versions';
import { AIImageEnhancementService } from '../lib/ai-image-enhancement';
import { CreativeEnhancementModal } from '../components/CreativeEnhancementModal';
import { ImagePreviewModal } from '../components/ImagePreviewModal';
import { ActivityService } from '../lib/activity';
import { StorageService } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { truncateAnalysisName } from '../lib/utils';
import LimitEnforcementService from '../lib/limit-enforcement-service';
import { useAuth } from '../contexts/AuthContext';
import EnhancedVersionComparison from '../components/EnhancedVersionComparison';
import { PlatformIcon } from '../components/PlatformIcons';
import AnalysisLoadingScreen from '../components/AnalysisLoadingScreen';

// Mock analysis data with performance analysis
const mockAnalysisData = {
  'analysis_v2_1': {
    id: 'analysis_v2_1',
    creativeName: 'Summer Sale Banner v2.1.jpg',
    creativeType: 'image',
    creativeUrl: 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800',
    analysisDate: '2024-01-15T10:30:00Z',
    overallScore: 92,
    creditsUsed: 1,
    version: 'v2.1',
    platforms: {
      meta: { score: 94, status: 'approved', issues: [] },
      google: { score: 91, status: 'approved', issues: ['Minor text overlay concern'] },
      youtube: { score: 89, status: 'approved', issues: [] },
      tiktok: { score: 87, status: 'warning', issues: ['Text overlay exceeds 20% limit'] },
      pinterest: { score: 96, status: 'approved', issues: [] },
      microsoft: { score: 93, status: 'approved', issues: [] }
    },
    // Performance Analysis Data
    performanceAnalysis: {
      overallPerformanceScore: 88,
      brandAwarenessScore: 91,
      platformPerformance: {
        meta: {
          performanceScore: 89,
          predictedCTR: 2.4,
          predictedEngagement: 4.2,
          strengths: ['Strong visual hierarchy', 'Clear CTA placement'],
          weaknesses: ['Could benefit from more emotional appeal']
        },
        google: {
          performanceScore: 85,
          predictedCTR: 2.1,
          predictedEngagement: 3.8,
          strengths: ['Professional appearance', 'Clear value proposition'],
          weaknesses: ['Text could be more prominent']
        },
        youtube: {
          performanceScore: 87,
          predictedCTR: 2.3,
          predictedEngagement: 4.0,
          strengths: ['Eye-catching design', 'Good contrast'],
          weaknesses: ['Could use more dynamic elements']
        },
        tiktok: {
          performanceScore: 82,
          predictedCTR: 1.9,
          predictedEngagement: 3.5,
          strengths: ['Vibrant colors', 'Youth appeal'],
          weaknesses: ['Text overlay too prominent', 'Needs more authenticity']
        },
        pinterest: {
          performanceScore: 93,
          predictedCTR: 3.1,
          predictedEngagement: 5.2,
          strengths: ['Perfect for Pinterest aesthetic', 'High visual appeal'],
          weaknesses: ['Minor branding improvement needed']
        },
        microsoft: {
          performanceScore: 86,
          predictedCTR: 2.2,
          predictedEngagement: 3.9,
          strengths: ['Professional design', 'Clear messaging'],
          weaknesses: ['Could be more engaging']
        }
      },
      keyFactors: {
        attention: {
          score: 89,
          description: 'Strong visual elements that capture attention effectively',
          details: ['Bright color scheme attracts the eye', 'Good use of contrast', 'Clear focal points']
        },
        clarity: {
          score: 92,
          description: 'Message and value proposition are very clear',
          details: ['Easy to read typography', 'Clear hierarchy', 'Obvious call-to-action']
        },
        engagement: {
          score: 85,
          description: 'Good potential for user interaction and engagement',
          details: ['Appealing visual style', 'Relevant to target audience', 'Emotional connection present']
        },
        branding: {
          score: 88,
          description: 'Strong brand presence and memorability',
          details: ['Consistent brand colors', 'Logo prominently placed', 'Brand voice clear']
        }
      },
      aiRecommendations: [
        {
          category: 'Performance',
          priority: 'high',
          title: 'Enhance Emotional Appeal',
          description: 'Add more emotional triggers to increase engagement rates across platforms.',
          impact: 'Could improve CTR by 15-20%',
          platforms: ['meta', 'google', 'youtube']
        },
        {
          category: 'Branding',
          priority: 'medium',
          title: 'Increase Logo Visibility',
          description: 'Make the logo 20% larger to improve brand recall and recognition.',
          impact: 'Could improve brand awareness by 12%',
          platforms: ['all']
        },
        {
          category: 'Platform-Specific',
          priority: 'high',
          title: 'Reduce Text Overlay for TikTok',
          description: 'Decrease text overlay to under 20% for better TikTok algorithm performance.',
          impact: 'Could improve reach by 25% on TikTok',
          platforms: ['tiktok']
        },
        {
          category: 'Engagement',
          priority: 'medium',
          title: 'Add Dynamic Elements',
          description: 'Consider subtle animations or gradient effects to increase visual interest.',
          impact: 'Could improve engagement by 10-15%',
          platforms: ['youtube', 'meta']
        }
      ],
      attentionHeatmap: {
        hotspots: [
          { x: 45, y: 30, intensity: 95, element: 'Main headline' },
          { x: 70, y: 60, intensity: 88, element: 'Call-to-action button' },
          { x: 20, y: 15, intensity: 82, element: 'Brand logo' },
          { x: 55, y: 75, intensity: 76, element: 'Product image' }
        ]
      }
    },
    detectedIssues: [
      {
        id: 1,
        platform: 'tiktok',
        severity: 'medium',
        title: 'Text Overlay Limit Exceeded',
        description: 'Text covers more than 20% of the image area, which may reduce reach on TikTok.',
        rule: 'TikTok Creative Guidelines - Text Overlay',
        ruleUrl: 'https://ads.tiktok.com/help/article?aid=9549',
        recommendation: 'Reduce text size or move text to a smaller area of the image.',
        location: 'Center area of the image'
      }
    ],
    recommendations: [
      'Consider reducing text overlay to improve TikTok performance',
      'The current design performs well across most platforms',
      'Color contrast is excellent for accessibility'
    ]
  },
  'analysis_v1_2': {
    id: 'analysis_v1_2',
    creativeName: 'Product Video Ad v1.2.mp4',
    creativeType: 'video',
    creativeUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
    analysisDate: '2024-01-14T15:20:00Z',
    overallScore: 78,
    creditsUsed: 1,
    version: 'v1.2',
    duration: 30, // seconds
    platforms: {
      meta: { score: 82, status: 'warning', issues: ['Audio compliance issue at 0:15'] },
      google: { score: 79, status: 'warning', issues: ['Text overlay violation at 0:08'] },
      youtube: { score: 85, status: 'approved', issues: [] },
      tiktok: { score: 71, status: 'warning', issues: ['Content too promotional at 0:22'] },
      pinterest: { score: 88, status: 'approved', issues: [] },
      microsoft: { score: 83, status: 'approved', issues: [] }
    },
    // Performance Analysis for Video
    performanceAnalysis: {
      overallPerformanceScore: 74,
      brandAwarenessScore: 79,
      platformPerformance: {
        meta: {
          performanceScore: 76,
          predictedCTR: 1.8,
          predictedEngagement: 3.2,
          predictedRetention: 65,
          strengths: ['Good opening hook', 'Clear product showcase'],
          weaknesses: ['Audio issues may hurt engagement', 'Pacing could be improved']
        },
        youtube: {
          performanceScore: 81,
          predictedCTR: 2.2,
          predictedEngagement: 4.1,
          predictedRetention: 72,
          strengths: ['Professional production quality', 'Good storytelling'],
          weaknesses: ['Could use more dynamic editing']
        },
        tiktok: {
          performanceScore: 68,
          predictedCTR: 1.4,
          predictedEngagement: 2.8,
          predictedRetention: 45,
          strengths: ['Relevant to audience'],
          weaknesses: ['Too promotional', 'Lacks authenticity', 'Slow start']
        }
      },
      keyFactors: {
        hook: {
          score: 72,
          description: 'Opening 3 seconds capture attention moderately well',
          details: ['Decent visual opening', 'Could be more compelling', 'Needs stronger hook']
        },
        pacing: {
          score: 76,
          description: 'Video pacing is acceptable but could be improved',
          details: ['Some slow moments', 'Good overall flow', 'Could use more dynamic cuts']
        },
        storytelling: {
          score: 81,
          description: 'Clear narrative structure with good product showcase',
          details: ['Logical progression', 'Clear value proposition', 'Good product demonstration']
        },
        retention: {
          score: 69,
          description: 'Moderate viewer retention expected',
          details: ['Some drop-off points identified', 'Could improve mid-video engagement']
        }
      },
      aiRecommendations: [
        {
          category: 'Hook',
          priority: 'high',
          title: 'Strengthen Opening Hook',
          description: 'Add a compelling question or surprising visual in the first 3 seconds.',
          impact: 'Could improve retention by 20-25%',
          platforms: ['all'],
          timestamp: 0
        },
        {
          category: 'Audio',
          priority: 'high',
          title: 'Fix Audio Levels',
          description: 'Normalize audio levels to prevent compliance issues and improve user experience.',
          impact: 'Required for platform approval',
          platforms: ['meta'],
          timestamp: 15
        },
        {
          category: 'Platform-Specific',
          priority: 'high',
          title: 'Make Content Less Promotional for TikTok',
          description: 'Add more entertainment value and authentic moments to reduce promotional feel.',
          impact: 'Could improve TikTok performance by 30%',
          platforms: ['tiktok'],
          timestamp: 22
        },
        {
          category: 'Engagement',
          priority: 'medium',
          title: 'Add Dynamic Transitions',
          description: 'Use more dynamic cuts and transitions to maintain viewer interest.',
          impact: 'Could improve retention by 10-15%',
          platforms: ['all']
        }
      ],
      videoInsights: {
        engagementCurve: [
          { time: 0, engagement: 100 },
          { time: 3, engagement: 85 },
          { time: 8, engagement: 70 },
          { time: 15, engagement: 60 },
          { time: 22, engagement: 45 },
          { time: 30, engagement: 40 }
        ],
        keyMoments: [
          { time: 8, type: 'issue', description: 'Text overlay violation' },
          { time: 15, type: 'issue', description: 'Audio compliance issue' },
          { time: 22, type: 'issue', description: 'Too promotional content' }
        ]
      }
    },
    detectedIssues: [
      {
        id: 1,
        platform: 'google',
        severity: 'high',
        title: 'Text Overlay Violation',
        description: 'Text overlay exceeds the 20% limit during this segment.',
        rule: 'Google Ads Creative Guidelines - Text in Images',
        ruleUrl: 'https://support.google.com/google-ads/answer/1722096',
        recommendation: 'Reduce text size or duration of text overlay.',
        timestamp: 8,
        timeDisplay: '00:08'
      },
      {
        id: 2,
        platform: 'meta',
        severity: 'medium',
        title: 'Audio Compliance Issue',
        description: 'Audio level may be too loud and could violate platform guidelines.',
        rule: 'Meta Advertising Policies - Audio Content',
        ruleUrl: 'https://www.facebook.com/policies/ads/prohibited_content/audio_content',
        recommendation: 'Reduce audio level or add audio normalization.',
        timestamp: 15,
        timeDisplay: '00:15'
      },
      {
        id: 3,
        platform: 'tiktok',
        severity: 'high',
        title: 'Overly Promotional Content',
        description: 'Content appears too sales-focused which may reduce organic reach.',
        rule: 'TikTok Creative Guidelines - Promotional Content',
        ruleUrl: 'https://ads.tiktok.com/help/article?aid=9550',
        recommendation: 'Make content more entertaining and less sales-focused.',
        timestamp: 22,
        timeDisplay: '00:22'
      }
    ],
    recommendations: [
      'Reduce text overlay duration to improve Google Ads compliance',
      'Normalize audio levels for better Meta performance',
      'Consider making the content more entertaining for TikTok'
    ]
  },
  'analysis_v1_0': {
    id: 'analysis_v1_0',
    creativeName: 'Instagram Story Template v1.0.jpg',
    creativeType: 'image',
    creativeUrl: 'https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=800',
    analysisDate: '2024-01-13T09:45:00Z',
    overallScore: 85,
    creditsUsed: 1,
    version: 'v1.0',
    platforms: {
      meta: { score: 88, status: 'approved', issues: [] },
      google: { score: 84, status: 'approved', issues: [] },
      youtube: { score: 82, status: 'approved', issues: [] },
      tiktok: { score: 89, status: 'approved', issues: [] },
      pinterest: { score: 87, status: 'approved', issues: [] },
      microsoft: { score: 81, status: 'warning', issues: ['Age verification required'] }
    },
    performanceAnalysis: {
      overallPerformanceScore: 83,
      brandAwarenessScore: 86,
      platformPerformance: {
        meta: {
          performanceScore: 85,
          predictedCTR: 2.1,
          predictedEngagement: 3.9,
          strengths: ['Perfect for Stories format', 'Good visual appeal'],
          weaknesses: ['Could use stronger CTA']
        },
        tiktok: {
          performanceScore: 87,
          predictedCTR: 2.3,
          predictedEngagement: 4.2,
          strengths: ['Authentic feel', 'Good for younger audience'],
          weaknesses: ['Minor branding improvements needed']
        }
      },
      keyFactors: {
        attention: { score: 84, description: 'Good attention-grabbing elements' },
        clarity: { score: 88, description: 'Very clear message and design' },
        engagement: { score: 81, description: 'Good engagement potential' },
        branding: { score: 79, description: 'Brand presence could be stronger' }
      },
      aiRecommendations: [
        {
          category: 'Branding',
          priority: 'medium',
          title: 'Strengthen Brand Presence',
          description: 'Increase logo size and add brand colors for better recognition.',
          impact: 'Could improve brand recall by 15%',
          platforms: ['all']
        },
        {
          category: 'Engagement',
          priority: 'medium',
          title: 'Add Interactive Elements',
          description: 'Consider adding polls or questions for Stories format.',
          impact: 'Could improve engagement by 20%',
          platforms: ['meta', 'tiktok']
        }
      ]
    },
    detectedIssues: [
      {
        id: 1,
        platform: 'microsoft',
        severity: 'low',
        title: 'Age Verification Required',
        description: 'Content may require age verification for certain claims.',
        rule: 'Microsoft Advertising Policies - Age-Restricted Content',
        ruleUrl: 'https://help.ads.microsoft.com/apex/index/3/en/60001',
        recommendation: 'Add age verification disclaimer or modify claims.',
        location: 'Bottom text area'
      }
    ],
    recommendations: [
      'Add age verification disclaimer for Microsoft Ads',
      'Overall design performs well across platforms',
      'Consider A/B testing different call-to-action text'
    ]
  }
};

// Version history is now loaded dynamically via state

const platformInfo = {
  meta: { name: 'Meta', color: 'bg-blue-500', textColor: 'text-blue-600' },
  google: { name: 'Google', color: 'bg-red-500', textColor: 'text-red-600' },
  youtube: { name: 'YouTube', color: 'bg-red-600', textColor: 'text-red-600' },
  tiktok: { name: 'TikTok', color: 'bg-black', textColor: 'text-gray-900' },
  pinterest: { name: 'Pinterest', color: 'bg-red-400', textColor: 'text-red-600' },
  microsoft: { name: 'Microsoft', color: 'bg-blue-600', textColor: 'text-blue-600' }
};

export function AnalysisPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const analysisId = searchParams.get('id');
  const projectId = searchParams.get('projectId'); // Get project context
  const creativeId = searchParams.get('creativeId'); // For direct creative analysis
  const versionOfCreative = searchParams.get('versionOfCreative'); // For creating new version
  const autoAnalyze = searchParams.get('autoAnalyze') === 'true'; // Auto-trigger analysis
  
  const [currentAnalysisId, setCurrentAnalysisId] = useState(analysisId || '');
  const [contextProjectId, setContextProjectId] = useState(projectId || ''); // Store project context
  const [originalCreativeForVersioning, setOriginalCreativeForVersioning] = useState(versionOfCreative || ''); // Store original creative for versioning
  const [showUpload, setShowUpload] = useState(!analysisId);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null); // Platform filter state
  
  // Sync currentAnalysisId with URL parameter changes
  useEffect(() => {
    if (analysisId !== currentAnalysisId) {
      setCurrentAnalysisId(analysisId || '');
    }
  }, [analysisId]); // Removed currentAnalysisId from dependencies to prevent circular updates

  // Sync version creation parameters with URL changes
  useEffect(() => {
    if (versionOfCreative !== originalCreativeForVersioning) {
      setOriginalCreativeForVersioning(versionOfCreative || '');
    }
    if (projectId !== contextProjectId) {
      setContextProjectId(projectId || '');
    }
  }, [versionOfCreative, projectId]); // Removed state variables from dependencies to prevent circular updates
  
  // Update showUpload based on URL changes
  useEffect(() => {
    if (!analysisId && !showUpload) {
      console.log('📄 No analysis ID in URL but upload is hidden - showing upload');
      setShowUpload(true);
    } else if (analysisId && showUpload) {
      console.log('📄 Analysis ID in URL but upload is showing - hiding upload');
      setShowUpload(false);
    }
  }, [analysisId, showUpload]);
  
  // Handle navigation state from ad generator
  useEffect(() => {
    const state = location.state as {
      uploadedFile?: File;
      preloadedImageUrl?: string;
      source?: string;
      generationId?: string;
      autoStartAnalysis?: boolean;
    } | null;
    
    // Skip if this is a version creation flow (handled by the version creation useEffect)
    if (state?.uploadedFile && state?.autoStartAnalysis && state?.source !== 'version-modal') {
      console.log('🚀 Auto-starting analysis from ad generator:', {
        fileName: state.uploadedFile.name,
        source: state.source,
        generationId: state.generationId
      });
      
      // Clear the location state to prevent re-running on navigation
      window.history.replaceState({}, document.title);
      
      // Trigger the file upload
      handleFileUpload(state.uploadedFile);
    }
  }, [location.state]);

  // Auto-trigger analysis for enhanced creatives
  useEffect(() => {
    // Skip if this is a version creation flow (already handled by file upload)
    const isVersionCreation = location.state?.source === 'version-modal';
    const originalCreativeForVersioning = new URLSearchParams(window.location.search).get('versionOfCreative');
    
    if (creativeId && autoAnalyze && !analysisId && user && !isVersionCreation && !originalCreativeForVersioning) {
      console.log('🚀 Auto-triggering analysis for enhanced creative:', creativeId);
      
      const triggerAnalysis = async () => {
        try {
          setIsAnalyzing(true);
          setAnalysisError(null);
          
          // Start analysis for the creative
          const result = await CreativeService.reanalyzeCreative(
            creativeId,
            undefined, // platforms (will analyze all by default)
            undefined, // campaignType
            undefined, // industry
            (progress) => {
              console.log('Auto-analysis progress:', progress);
              setUploadProgress(progress.progress);
              setReanalyzeMessage(progress.message);
            }
          );
          
          if (result.analysisId) {
            console.log('✅ Auto-analysis completed successfully');
            
            // Navigate to the analysis results
            const analysisId = result.analysisId;
            if (analysisId) {
              // Update URL to show the analysis results
              navigate(`/dashboard/analysis?id=${analysisId}`, { replace: true });
            }
          } else {
            console.error('❌ Auto-analysis failed: No analysis ID returned');
            setAnalysisError('Analysis failed');
          }
        } catch (error) {
          console.error('❌ Auto-analysis error:', error);
          setAnalysisError(error instanceof Error ? error.message : 'Analysis failed');
        } finally {
          setIsAnalyzing(false);
          setUploadProgress(0);
          setReanalyzeMessage('');
        }
      };
      
      // Small delay to ensure the page is ready
      setTimeout(triggerAnalysis, 100);
    }
  }, [creativeId, autoAnalyze, analysisId, user, navigate, location.state]);
  
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [comparison, setComparison] = useState<VersionComparison | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [comparingVersions, setComparingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<string | null>(null);
  const [showRestoreConfirmModal, setShowRestoreConfirmModal] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<any>(null);
  const [showUndoNotification, setShowUndoNotification] = useState(false);
  const [undoTimeout, setUndoTimeout] = useState<NodeJS.Timeout | null>(null);
  const [previousCurrentVersion, setPreviousCurrentVersion] = useState<string | null>(null);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isVersionDragging, setIsVersionDragging] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showAddCreativeModal, setShowAddCreativeModal] = useState(false); // New modal for project context
  const [showEnhancementModal, setShowEnhancementModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showEnhancedImage, setShowEnhancedImage] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [autoProcessingVersion, setAutoProcessingVersion] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('compliance'); // compliance, performance
  
  // Real analysis data state
  const [realAnalysisData, setRealAnalysisData] = useState<AnalysisResult | null>(null);
  const [loadingRealData, setLoadingRealData] = useState(false);
  const [realDataError, setRealDataError] = useState<string | null>(null);
  const [enhancedImageUrl, setEnhancedImageUrl] = useState<string | null>(null);
  const [enhancedVersions, setEnhancedVersions] = useState<any[]>([]);
  const [selectedEnhancedVersion, setSelectedEnhancedVersion] = useState<number>(0);

  // Rename functionality state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [renamingLoading, setRenamingLoading] = useState(false);

  // Project management state
  const [projects, setProjects] = useState<ProjectWithMembers[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [addingToProject, setAddingToProject] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);

  // Export functionality state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Share functionality state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareNotification, setShareNotification] = useState<string | null>(null);

  // Add state for video metadata near other video states
  const [videoMetadata, setVideoMetadata] = useState<{
    width?: number;
    height?: number;
    aspectRatio?: number;
  }>({});
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoStreamUrl, setVideoStreamUrl] = useState<string | null>(null);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const [versionHistory, setVersionHistory] = useState<any[]>([]);
  const [loadingVersionHistory, setLoadingVersionHistory] = useState(false);
  const [versionDescription, setVersionDescription] = useState('');
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [currentCreativeId, setCurrentCreativeId] = useState<string | null>(null);

  // Secure URL handling state
  const [secureCreativeUrl, setSecureCreativeUrl] = useState<string | null>(null);
  const [secureEnhancedUrl, setSecureEnhancedUrl] = useState<string | null>(null);
  const [loadingSecureUrls, setLoadingSecureUrls] = useState(false);
  
  // Re-analyze state
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
  const [reanalyzeProgress, setReanalyzeProgress] = useState(0);
  const [reanalyzeMessage, setReanalyzeMessage] = useState('');
  
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const videoPreviewRef = React.useRef<HTMLDivElement>(null);

  // Function to load version history for a creative
  const loadVersionHistory = async (creativeId: string) => {
    try {
      setLoadingVersionHistory(true);
      const versions = await VersionService.getVersionHistory(creativeId);
      
      // Transform to match the UI structure and fetch missing analysis data
      const transformedVersions = await Promise.all(versions.map(async (v) => {
        let score = 0;
        
        // If analysis data exists in the creative record, use it
        if (v.analysisData && v.analysisData.overallScore) {
          score = v.analysisData.overallScore;
          console.log(`✅ Version ${v.versionNumber}: Using analysis data from creative record (${score}%)`);
        } else {
          // If no analysis data in creative record, try to fetch from analyses table
          try {
            const analysisData = await AIAnalysisService.getAnalysisForCreative(v.id);
            if (analysisData && analysisData.overallScore) {
              score = analysisData.overallScore;
              console.log(`✅ Version ${v.versionNumber}: Found analysis data in analyses table (${score}%)`);
            } else {
              console.log(`❌ Version ${v.versionNumber}: No analysis data found in either location`);
            }
          } catch (analysisError) {
            console.log(`⚠️ Version ${v.versionNumber}: Error fetching analysis data:`, analysisError.message);
          }
        }
        
        return {
          id: v.id,
          version: `v${v.versionNumber}`,
          date: v.createdAt,
          author: 'AI Analysis', // Could be enhanced with user info
          description: v.versionNotes || `Version ${v.versionNumber}`,
          score: score,
          status: v.isCurrentVersion ? 'current' : 'archived'
        };
      }));
      
      setVersionHistory(transformedVersions);
    } catch (error) {
      console.error('Failed to load version history:', error);
    } finally {
      setLoadingVersionHistory(false);
    }
  };

  // Function to load enhanced versions for analysis
  const loadEnhancedVersions = async (analysisId: string) => {
    try {
      console.log('🔍 Loading enhanced versions for analysis:', analysisId);
      const { data: versions, error } = await supabase
        .from('enhanced_versions')
        .select('*')
        .eq('analysis_id', analysisId)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error loading enhanced versions:', error);
        return;
      }
      
      if (versions && versions.length > 0) {
        setEnhancedVersions(versions);
        setEnhancedImageUrl(versions[0].enhanced_image_url); // Set the most recent one
        setSelectedEnhancedVersion(0);
        console.log(`✅ Loaded ${versions.length} enhanced versions`);
      } else {
        console.log('📭 No enhanced versions found for this analysis');
        setEnhancedVersions([]);
      }
    } catch (error) {
      console.error('Failed to load enhanced versions:', error);
      setEnhancedVersions([]);
    }
  };

  // Function to handle re-analysis of current creative
  const handleReanalyze = async () => {
    // Check if we're using mock data
    if (!realAnalysisData && currentAnalysisId && mockAnalysisData[currentAnalysisId as keyof typeof mockAnalysisData]) {
      setReanalyzeError('Re-analysis is not available for demo data. Please upload your own creative to use this feature.');
      setTimeout(() => setReanalyzeError(null), 5000);
      return;
    }

    if (!currentCreativeId) {
      setReanalyzeError('No creative ID available for re-analysis');
      return;
    }

    try {
      setIsReanalyzing(true);
      setReanalyzeError(null);
      setReanalyzeProgress(0);
      setReanalyzeMessage('Initializing re-analysis...');

      // Check if user has enough credits
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const creditCheck = await LimitEnforcementService.canPerformAnalysis(user.id);
      if (!creditCheck.allowed) {
        throw new Error(creditCheck.reason || 'Insufficient credits for re-analysis');
      }

      console.log('🔄 Starting re-analysis for creative:', currentCreativeId);

      // Perform re-analysis using CreativeService
      const analysisResult = await CreativeService.reanalyzeCreative(
        currentCreativeId,
        undefined, // platforms (will analyze all by default)
        undefined, // campaignType
        undefined, // industry
        (progress) => {
          console.log('Re-analysis progress:', progress);
          setReanalyzeProgress(progress.progress);
          setReanalyzeMessage(progress.message);
        }
      );

      console.log('✅ Re-analysis completed:', analysisResult);

      // Navigate to the new analysis page
      if (analysisResult.analysisId) {
        console.log('🧭 Redirecting to new analysis:', analysisResult.analysisId);
        navigate(`/dashboard/analysis?id=${analysisResult.analysisId}`);
      }

    } catch (error) {
      console.error('Failed to re-analyze creative:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to re-analyze creative';
      setReanalyzeError(errorMessage);

      // Clear error after 5 seconds
      setTimeout(() => {
        setReanalyzeError(null);
      }, 5000);
    } finally {
      setIsReanalyzing(false);
      setReanalyzeProgress(0);
      setReanalyzeMessage('');
    }
  };

  // Function to handle version creation
  const handleCreateVersion = async () => {
    if (!versionFile || !currentCreativeId) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setCreatingVersion(true);
      
      // Use the same approach as AdGeneratorPage - pass the file directly via navigate state
      // This avoids issues with data URL corruption in sessionStorage
      console.log('🚀 Creating version using direct file transfer approach');
      
      const params = new URLSearchParams();
      params.set('versionOfCreative', currentCreativeId);
      if (versionDescription) {
        params.set('versionNotes', versionDescription);
      }
      params.set('autoStartAnalysis', 'true'); // Auto-start analysis flag
      
      // Navigate with file in state - more reliable than sessionStorage
      navigate(`/dashboard/analysis?${params.toString()}`, {
        state: {
          uploadedFile: versionFile,
          source: 'version-modal',
          originalCreativeId: currentCreativeId,
          versionNotes: versionDescription,
          autoStartAnalysis: true
        }
      });
      
    } catch (error) {
      console.error('Failed to create version:', error);
      alert('Failed to create version. Please try again.');
    } finally {
      setCreatingVersion(false);
      setShowNewVersionModal(false);
    }
  };

  // Load real analysis data when analysisId is provided, or handle project context
  useEffect(() => {
    
    // If analysisId changed from having a value to null, reset everything
    if (!analysisId && (currentAnalysisId || realAnalysisData)) {
      console.log('📄 Analysis ID removed from URL - resetting to upload mode');
      resetAnalysisState(false); // Don't reset currentAnalysisId as it's handled by sync effect
      return;
    }
    
    if (analysisId) {
      // Add a maximum loading time safeguard (30 seconds)
      const maxLoadingTimeout = setTimeout(() => {
        console.log('⚠️ Maximum loading time exceeded, forcing loading state off');
        setLoadingRealData(false);
        if (!realAnalysisData && !mockAnalysisData[analysisId as keyof typeof mockAnalysisData]) {
          setRealDataError('Loading timeout - please try again');
        }
      }, 30000);
      
      const loadRealAnalysisData = async () => {
        setLoadingRealData(true);
        setRealDataError(null);
        
        try {
          console.log('🔍 Loading analysis data for ID:', analysisId);
          const result = await AIAnalysisService.getAnalysisById(analysisId);
          
          if (result) {
            console.log('✅ Analysis data loaded successfully');
            // Store both the analysis data and creative URL
            const analysisWithUrl = {
              ...result.analysis,
              creativeUrl: result.creativeUrl,
              creativeId: result.creativeId,
              versionNumber: result.versionNumber
            };
            setRealAnalysisData(analysisWithUrl);
            
            // Load enhanced versions from enhanced_versions table
            await loadEnhancedVersions(analysisId);
            
            // Load enhanced image URL if available (legacy support)
            if (result.enhancedImageUrl) {
              setEnhancedImageUrl(result.enhancedImageUrl);
            }
            setCurrentAnalysisId(analysisId);
            setShowUpload(false);
            setRealDataError(null); // Clear any previous errors
            
            // Load version history if we have a creative ID
            if (result.creativeId) {
              console.log('📚 Loading version history for creative:', result.creativeId);
              setCurrentCreativeId(result.creativeId);
              loadVersionHistory(result.creativeId);
            }
          } else {
            console.log('⚠️ Analysis not found in database, checking mock data...');
            // Try mock data as fallback
            if (mockAnalysisData[analysisId as keyof typeof mockAnalysisData]) {
              console.log('✅ Found in mock data');
              setCurrentAnalysisId(analysisId);
              setShowUpload(false);
              setRealAnalysisData(null); // Clear real data to use mock
              setRealDataError(null); // Clear error since we found mock data
            } else {
              console.log('❌ Analysis not found in mock data either');
              setRealDataError('Analysis not found');
              setRealAnalysisData(null);
              setCurrentAnalysisId('');
              // If analysis not found, show upload interface
              setShowUpload(true);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching analysis:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to load analysis data';
          setRealDataError(errorMessage);
          setRealAnalysisData(null);
          
          // Try mock data as fallback
          if (mockAnalysisData[analysisId as keyof typeof mockAnalysisData]) {
            console.log('✅ Using mock data as fallback');
            setCurrentAnalysisId(analysisId);
            setShowUpload(false);
            setRealDataError(null); // Clear error since we found mock data
          } else {
            setCurrentAnalysisId('');
            // If analysis not found, show upload interface
            setShowUpload(true);
          }
        } finally {
          setLoadingRealData(false);
          console.log('🏁 Loading analysis data completed');
          clearTimeout(maxLoadingTimeout); // Clear the timeout when loading completes
        }
      };

      loadRealAnalysisData();
      
      // Cleanup function to clear timeout on unmount or re-run
      return () => {
        clearTimeout(maxLoadingTimeout);
      };
    } else if (projectId || versionOfCreative) {
      // If coming from project context or creating new version, show upload interface
      console.log('🏗️ Project context detected:', { projectId, versionOfCreative });
      if (versionOfCreative) {
        // Creating new version - check for file in navigation state first, then sessionStorage as fallback
        const navigationState = location.state as any;
        const uploadedFile = navigationState?.uploadedFile;
        const storedFile = sessionStorage.getItem('versionFile');
        
        setShowAddCreativeModal(false);
        setOriginalCreativeForVersioning(versionOfCreative);
        
        if (uploadedFile && navigationState?.source === 'version-modal') {
          // File passed via navigation state (new approach) - more reliable
          console.log('📁 File found in navigation state - setting auto-processing state');
          setAutoProcessingVersion(true);
          setShowUpload(false);
          setUploadProgress(1); // Start with minimal progress
          setIsAnalyzing(false); // Will be set to true when analysis starts
          
          // Auto-start the upload and analysis immediately
          console.log('🚀 Auto-starting upload for version creation with navigation state file');
          setTimeout(() => {
            handleFileUpload(uploadedFile);
          }, 100); // Small delay to ensure state is set
          
        } else if (storedFile) {
          // Fallback to sessionStorage (legacy approach) 
          console.log('📁 File found in sessionStorage - setting auto-processing state');
          setAutoProcessingVersion(true);
          setShowUpload(false);
          setUploadProgress(1); // Start with minimal progress
          setIsAnalyzing(false); // Will be set to true when analysis starts
          
          try {
            const fileData = JSON.parse(storedFile);
            console.log('📁 Auto-loading stored file for version creation:', fileData.name);
            
            // Convert base64 back to File object and auto-start upload
            fetch(fileData.data)
              .then(res => res.blob())
              .then(blob => {
                const file = new File([blob], fileData.name, { type: fileData.type });
                sessionStorage.removeItem('versionFile'); // Clean up
                
                // Auto-start the upload and analysis
                console.log('🚀 Auto-starting upload for version creation');
                handleFileUpload(file);
              })
              .catch(error => {
                console.error('❌ Failed to process stored file:', error);
                sessionStorage.removeItem('versionFile');
                // Reset states and show upload interface on error
                setAutoProcessingVersion(false);
                setShowUpload(true);
                setUploadProgress(0);
                setIsAnalyzing(false);
                setAnalysisError('Failed to process uploaded file. Please try uploading again.');
              });
          } catch (error) {
            console.error('❌ Failed to parse stored file:', error);
            sessionStorage.removeItem('versionFile');
            // Reset states and show upload interface on error
            setAutoProcessingVersion(false);
            setShowUpload(true);
            setUploadProgress(0);
            setIsAnalyzing(false);
            setAnalysisError('Failed to load uploaded file. Please try uploading again.');
          }
        } else {
          // No file found - show upload interface
          console.log('❌ No file found in navigation state or sessionStorage - showing upload interface');
          setAutoProcessingVersion(false);
          setShowUpload(true);
        }
        
        // Fetch original creative's project context to ensure new version inherits it
        const fetchOriginalCreativeContext = async () => {
          try {
            console.log('🔍 Fetching original creative context for versioning:', versionOfCreative);
            const { data: originalCreative, error } = await supabase
              .from('creatives')
              .select('project_id')
              .eq('id', versionOfCreative)
              .single();
              
            if (error) {
              console.error('❌ Failed to fetch original creative context:', error);
            } else {
              console.log('📊 Original creative data:', originalCreative);
              if (originalCreative?.project_id) {
                console.log('🏗️ Setting project context from original creative:', originalCreative.project_id);
                setContextProjectId(originalCreative.project_id);
              } else {
                console.log('ℹ️ Original creative has no project_id - this is an individual creative');
              }
            }
          } catch (error) {
            console.error('❌ Error fetching original creative context:', error);
          }
        };
        
        fetchOriginalCreativeContext();
      } else {
        // Coming from project context - show add creative modal
        setShowAddCreativeModal(true);
        setShowUpload(false);
      }
      setCurrentAnalysisId('');
      setRealAnalysisData(null);
      setLoadingRealData(false);
      setRealDataError(null);
    } else {
      // If no analysisId or projectId, show upload interface
      setShowUpload(true);
      setShowAddCreativeModal(false);
      setCurrentAnalysisId('');
      setRealAnalysisData(null);
      setLoadingRealData(false); // Ensure loading state is off
      setRealDataError(null); // Clear any errors
    }
  }, [analysisId, projectId, versionOfCreative, currentAnalysisId, location.state]);

  // Safety check: ensure upload field appears when there's no analysis
  useEffect(() => {
    // Add a small delay to ensure all state updates are processed
    const safetyTimeout = setTimeout(() => {
      if (!analysisId && !projectId && !versionOfCreative && !loadingRealData && !realAnalysisData && !currentAnalysisId) {
        if (!showUpload) {
          console.log('⚠️ Upload field should be visible but is not - forcing it to show');
          setShowUpload(true);
        }
      }
    }, 100); // Small delay to let other state updates complete

    return () => clearTimeout(safetyTimeout);
  }, [analysisId, projectId, versionOfCreative, loadingRealData, realAnalysisData, currentAnalysisId, showUpload]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportDropdown) {
        const target = event.target as Element;
        if (!target.closest('.relative')) {
          setShowExportDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown]);

  // Get current analysis data (prefer real data over mock data)
  const currentAnalysis = realAnalysisData ? transformRealToMockFormat(realAnalysisData) : 
    (currentAnalysisId ? mockAnalysisData[currentAnalysisId as keyof typeof mockAnalysisData] : null);

  // Load secure URLs for all creative assets
  useEffect(() => {
    const loadSecureUrls = async () => {
      if (!currentAnalysis?.creativeUrl) {
        setSecureCreativeUrl(null);
        setVideoStreamUrl(null);
        return;
      }

      setLoadingSecureUrls(true);
      try {
        console.log('🔒 Loading public URLs...');
        
        // URLs are now public, use them directly
        setSecureCreativeUrl(currentAnalysis.creativeUrl);

        // For videos, also get streaming URL with extended expiration
        if (currentAnalysis?.creativeType === 'video') {
          console.log('🎬 Loading video streaming URL...');
          const streamUrl = await StorageService.getVideoStreamingUrl(currentAnalysis.creativeUrl);
          setVideoStreamUrl(streamUrl);
        } else {
          setVideoStreamUrl(null);
        }
        
        console.log('✅ Secure URLs loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load secure URLs:', error);
        // Fallback to original URL
        setSecureCreativeUrl(currentAnalysis.creativeUrl);
        if (currentAnalysis?.creativeType === 'video') {
          setVideoStreamUrl(currentAnalysis.creativeUrl);
        }
      } finally {
        setLoadingSecureUrls(false);
      }
    };

    loadSecureUrls();
  }, [currentAnalysis?.creativeUrl, currentAnalysis?.creativeType]);

  // Handle enhanced image URL security
  useEffect(() => {
    const loadSecureEnhancedUrl = async () => {
      if (!enhancedImageUrl) {
        setSecureEnhancedUrl(null);
        return;
      }

      // URLs are now public, use them directly
      setSecureEnhancedUrl(enhancedImageUrl);
    };

    loadSecureEnhancedUrl();
  }, [enhancedImageUrl]);

  // Handle video URL changes - reload video element when URL changes
  useEffect(() => {
    const videoUrl = videoStreamUrl || secureCreativeUrl || currentAnalysis?.creativeUrl;

    if (videoRef.current && videoUrl && currentAnalysis?.creativeType === 'video') {
      console.log('🔄 Video URL changed, reloading video element:', videoUrl);
      console.log('🔍 Video element current src BEFORE load:', videoRef.current.src);
      console.log('🔍 Video element current src BEFORE load (attribute):', videoRef.current.getAttribute('src'));
      videoRef.current.load();

      // Check src after load
      setTimeout(() => {
        if (videoRef.current) {
          console.log('🔍 Video element src AFTER load:', videoRef.current.src);
          console.log('🔍 Video element src AFTER load (attribute):', videoRef.current.getAttribute('src'));
        }
      }, 100);
    }
  }, [videoStreamUrl, secureCreativeUrl, currentAnalysis?.creativeUrl, currentAnalysis?.creativeType]);

  // Note: Session refresh removed - buckets are now public and don't require authentication

  // Transform real analysis data to match mock data format for display
  function transformRealToMockFormat(realData: AnalysisResult & {creativeUrl?: string, creativeId?: string}): any {
    // Get creative URL from the enhanced real data
    // Log for debugging
    console.log('🔍 transformRealToMockFormat - creativeUrl:', realData.creativeUrl);
    console.log('🔍 transformRealToMockFormat - fileInfo:', realData.fileInfo);

    const creativeUrl = realData.creativeUrl || '';

    // Get version information from the current creative
    const getVersionInfo = () => {
      // Check version history lookup
      if (realData.creativeId && versionHistory.length > 0) {
        const currentVersion = versionHistory.find(v => v.id === realData.creativeId);
        if (currentVersion) {
          return currentVersion.version;
        }
      }
      
      // Final fallback - if we're viewing a version, try to get it from URL context
      if (versionOfCreative && realData.creativeId) {
        // This means we're viewing a specific version, default to v1 for now
        return 'v1';
      }
      
      return 'v1'; // Fallback to v1 if no version info found
    };

    // Transform platform data to match expected UI format
    const transformedPlatforms: Record<string, any> = {};
    if (realData.platforms) {
      Object.entries(realData.platforms).forEach(([platformName, platformData]) => {
        // Use same calculation as Recent Analysis widget: average of compliance and performance scores
        const complianceScore = platformData.complianceScore || 0;
        const performanceScore = platformData.performanceScore || 0;
        const averageScore = Math.round((complianceScore + performanceScore) / 2);
        
        transformedPlatforms[platformName] = {
          score: averageScore, // Use average score to match Recent Analysis widget
          complianceScore: complianceScore, // Keep individual scores available
          performanceScore: performanceScore,
          status: platformData.status || 'unknown',
          issues: platformData.issues || [],
          predictedCTR: platformData.predictedCTR || 0,
          predictedEngagement: platformData.predictedEngagement || 0,
          algorithmFavorability: platformData.algorithmFavorability || 0,
          strengths: platformData.strengths || [],
          weaknesses: platformData.weaknesses || []
        };
      });
    }

    return {
      id: realData.analysisId,
      creativeName: realData.fileInfo?.name || 'Creative',
      creativeType: realData.fileInfo?.type?.startsWith('video') ? 'video' : 'image',
      creativeUrl: creativeUrl,
      analysisDate: realData.timestamp || new Date().toISOString(),
      overallScore: realData.overallScore,
      creditsUsed: 1,
      version: getVersionInfo(),
      platforms: transformedPlatforms,
      performanceAnalysis: realData.performanceAnalysis || {
        overallPerformanceScore: realData.overallScore,
        brandAwarenessScore: realData.overallScore,
        platformPerformance: {},
        keyFactors: {},
        aiRecommendations: realData.aiRecommendations || []
      },
      detectedIssues: realData.detectedIssues || [],
      aiRecommendations: realData.aiRecommendations || [], // ✅ Added missing AI recommendations!
      videoDuration: realData.videoDuration,
      timelineAnalysis: realData.timelineAnalysis,
      audioAnalysis: realData.audioAnalysis
    };
  }

  // Helper function to format CTR and engagement percentages
  const formatPercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return '0.00';
    // All values from Gemini are already percentages, just format them properly
    return value.toFixed(2);
  };

  // Helper function to get platform data with fallbacks
  const getPlatformData = (platformName: string) => {
    // Normalize platform name to match platformInfo keys
    const normalizedName = platformName.toLowerCase().replace(/\s+/g, '').replace('ads', '').replace('googleads', 'google');
    const platformData = platformInfo[normalizedName as keyof typeof platformInfo];
    
    // Return platform data or fallback
    return platformData || {
      name: platformName,
      color: 'bg-gray-500',
      textColor: 'text-gray-600'
    };
  };

  // Unified color function based on score percentage
  const getScoreColor = (score: number) => {
    if (score >= 80) return { bar: 'bg-green-500', status: 'text-green-600 bg-green-100' };
    if (score >= 50) return { bar: 'bg-yellow-500', status: 'text-yellow-600 bg-yellow-100' };
    return { bar: 'bg-red-500', status: 'text-red-600 bg-red-100' };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'needs_review': return 'text-yellow-600 bg-yellow-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatStatus = (status: string) => {
    if (!status) return 'Unknown';
    
    // Handle specific cases first
    if (status === 'needs_review') return 'Needs Review';
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    
    // Fallback to general formatting
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'rejected': return XCircle;
      default: return Clock;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPerformanceGrade = (score: number) => {
    if (score >= 90) return { grade: 'A+', color: 'text-green-600' };
    if (score >= 80) return { grade: 'A', color: 'text-green-600' };
    if (score >= 70) return { grade: 'B', color: 'text-blue-600' };
    if (score >= 60) return { grade: 'C', color: 'text-yellow-600' };
    return { grade: 'D', color: 'text-red-600' };
  };

  const handleHistoryClick = async (creativeId: string) => {
    try {
      console.log('🔍 Handling version history click for creative:', creativeId);
      setShowVersionHistory(false);
      setShowUpload(false);
      
      // Find the analysis that belongs to this creative
      const { data: analysis, error } = await supabase
        .from('analyses')
        .select('id')
        .eq('creative_id', creativeId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (error || !analysis) {
        console.error('❌ No analysis found for creative:', creativeId);
        // If no analysis found, we might need to show the creative directly
        // For now, let's show an error message
        alert('No analysis found for this version. The creative may not have been analyzed yet.');
        return;
      }
      
      console.log('✅ Found analysis for creative:', { creativeId, analysisId: analysis.id });
      setCurrentAnalysisId(analysis.id);
      navigate(`/dashboard/analysis?id=${analysis.id}`);
    } catch (error) {
      console.error('❌ Error handling version history click:', error);
      alert('Failed to load version. Please try again.');
    }
  };

  // Handle recent analyses widget clicks (different from version history clicks)
  const handleRecentAnalysisClick = async (analysisId: string) => {
    try {
      console.log('🔍 Handling recent analysis click for analysis:', analysisId);
      setShowVersionHistory(false);
      setShowUpload(false);
      setCurrentAnalysisId(analysisId);
      navigate(`/dashboard/analysis?id=${analysisId}`);
    } catch (error) {
      console.error('❌ Error handling recent analysis click:', error);
      alert('Failed to load analysis. Please try again.');
    }
  };

  // Handle version selection for comparison
  const handleVersionSelection = (versionId: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 2) {
        return [...prev, versionId];
      } else {
        // Replace the first selected version
        return [prev[1], versionId];
      }
    });
  };

  // Handle version comparison
  const handleCompareVersions = async () => {
    if (selectedVersions.length !== 2) return;

    try {
      setComparingVersions(true);
      const comparisonData = await VersionService.compareVersions(selectedVersions[0], selectedVersions[1]);
      setComparison(comparisonData);
      setShowComparison(true);
    } catch (err) {
      console.error('Failed to compare versions:', err);
      alert('Failed to compare versions. Please try again.');
    } finally {
      setComparingVersions(false);
    }
  };

  // Handle restore version click - opens confirmation modal
  const handleSetCurrentVersion = (version: any) => {
    setVersionToRestore(version);
    setShowRestoreConfirmModal(true);
  };

  // Execute the actual restore operation
  const executeRestore = async () => {
    if (!versionToRestore) return;

    try {
      setRestoringVersion(versionToRestore.id);
      setShowRestoreConfirmModal(false);
      
      // Store the current version for undo functionality
      const currentVersion = versionHistory.find(v => v.status === 'current');
      setPreviousCurrentVersion(currentVersion?.id || null);
      
      // Set the version as current
      await VersionService.setCurrentVersion(versionToRestore.id);
      
      // Show undo notification
      setShowUndoNotification(true);
      
      // Set timeout to hide undo notification after 10 seconds
      const timeout = setTimeout(() => {
        setShowUndoNotification(false);
        setPreviousCurrentVersion(null);
      }, 10000);
      setUndoTimeout(timeout);
      
      // Find the analysis for the restored version
      const { data: analysis, error } = await supabase
        .from('analyses')
        .select('id')
        .eq('creative_id', versionToRestore.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (analysis && !error) {
        // Navigate to the restored version's analysis
        console.log('✅ Version restored successfully, navigating to analysis:', analysis.id);
        navigate(`/dashboard/analysis?id=${analysis.id}`);
      } else {
        console.warn('⚠️ No analysis found for restored version:', versionToRestore.id);
        // If no analysis found, just reload the current page to show updated version history
        window.location.reload();
      }
      
    } catch (err) {
      console.error('Failed to set current version:', err);
      alert('Failed to restore version. Please try again.');
      setRestoringVersion(null); // Reset loading state on error
    }
  };

  // Handle undo restore
  const handleUndoRestore = async () => {
    if (!previousCurrentVersion) return;

    try {
      // Clear all restore-related states
      setRestoringVersion(null);
      setShowUndoNotification(false);
      
      // Clear the timeout
      if (undoTimeout) {
        clearTimeout(undoTimeout);
        setUndoTimeout(null);
      }
      
      // Restore the previous current version
      await VersionService.setCurrentVersion(previousCurrentVersion);
      
      // Clear previous version state
      setPreviousCurrentVersion(null);
      
      // Find the analysis for the previous version
      const { data: analysis, error } = await supabase
        .from('analyses')
        .select('id')
        .eq('creative_id', previousCurrentVersion)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (analysis && !error) {
        // Navigate back to the previous version's analysis
        navigate(`/dashboard/analysis?id=${analysis.id}`);
      } else {
        // If no analysis found, just reload the current page
        window.location.reload();
      }
      
    } catch (err) {
      console.error('Failed to undo version restore:', err);
      alert('Failed to undo restore. Please try again.');
    }
  };

  // Rename functionality handlers
  const handleStartRename = () => {
    setEditingName(currentAnalysis?.creativeName || '');
    setIsEditingName(true);
  };

  const handleCancelRename = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const handleSaveRename = async () => {
    if (!analysisId || !editingName.trim() || editingName === currentAnalysis?.creativeName) {
      setIsEditingName(false);
      return;
    }

    setRenamingLoading(true);
    try {
      await RecentAnalysesService.renameAnalysis(analysisId, editingName.trim());
      
      // Update the current analysis data
      if (realAnalysisData) {
        setRealAnalysisData({
          ...realAnalysisData,
          fileInfo: {
            ...realAnalysisData.fileInfo,
            name: editingName.trim()
          }
        });
      }
      
      // Also update mock data if it exists
      if (mockAnalysisData[analysisId as keyof typeof mockAnalysisData]) {
        (mockAnalysisData[analysisId as keyof typeof mockAnalysisData] as any).creativeName = editingName.trim();
      }
      
      setIsEditingName(false);
      setEditingName('');
    } catch (error) {
      console.error('Error renaming analysis:', error);
      // Could show a toast notification here
    } finally {
      setRenamingLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  };

  const handleTimestampClick = (timestamp: number) => {
    setCurrentVideoTime(timestamp);
    // Actually seek the video to the timestamp
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp;
      // Ensure video starts playing if it was paused
      videoRef.current.play().catch(console.error);
    }
    
    // Scroll to video preview with smooth animation
    if (videoPreviewRef.current) {
      videoPreviewRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
    
    console.log(`Seeking to ${timestamp} seconds and scrolling to video preview`);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle video loading errors
  const handleVideoLoadError = async () => {
    console.log('⚠️ Video loading error occurred');
    // Since buckets are public, we don't need session refresh
    // Just try to reload the video element
    if (videoRef.current) {
      console.log('🔄 Attempting to reload video...');
      const currentSrc = videoRef.current.currentSrc;
      if (currentSrc) {
        videoRef.current.load();
      } else {
        console.error('❌ No video source URL available');
      }
    }
  };

  const getTimelineMarkers = () => {
    if (!currentAnalysis || currentAnalysis.creativeType !== 'video') return [];
    
    const markers: Array<{
      timestamp: number;
      type: 'issue' | 'moment' | 'recommendation';
      severity?: string;
      title: string;
      description?: string;
      engagement?: number;
    }> = [];

    // Add detected issues
    currentAnalysis.detectedIssues
      .filter((issue: any) => issue.timestamp !== undefined)
      .forEach((issue: any) => {
        markers.push({
          timestamp: issue.timestamp!,
          type: 'issue',
          severity: issue.severity,
          title: issue.title,
          description: issue.description
        });
      });

    // Add key moments from timeline analysis
    if (currentAnalysis.timelineAnalysis?.keyMoments) {
      currentAnalysis.timelineAnalysis.keyMoments.forEach((moment: any) => {
        markers.push({
          timestamp: moment.timestamp,
          type: 'moment',
          title: moment.description,
          engagement: moment.attentionLevel
        });
      });
    }

    // Add AI recommendations with timestamps
    if (currentAnalysis.aiRecommendations) {
      currentAnalysis.aiRecommendations
        .filter((rec: any) => rec.timestamp !== undefined)
        .forEach((rec: any) => {
          markers.push({
            timestamp: rec.timestamp!,
            type: 'recommendation',
            severity: rec.priority,
            title: rec.title,
            description: rec.description
          });
        });
    }

    return markers.sort((a, b) => a.timestamp - b.timestamp);
  };

  // Get engagement level at specific timestamp for heatmap
  const getEngagementAtTime = (timestamp: number): number => {
    if (!currentAnalysis?.timelineAnalysis?.engagementCurve) return 50;
    
    const curve = currentAnalysis.timelineAnalysis.engagementCurve;
    const duration = currentAnalysis.videoDuration || videoDuration || 30;
    
    // Find the closest data point
    const timePercentage = timestamp / duration;
    const index = Math.round(timePercentage * (curve.length - 1));
    const clampedIndex = Math.max(0, Math.min(index, curve.length - 1));
    
    return curve[clampedIndex]?.engagement || 50;
  };

  // Generate engagement heatmap segments for the timeline
  const getEngagementHeatmap = () => {
    if (!currentAnalysis?.timelineAnalysis?.engagementCurve) return [];
    
    const curve = currentAnalysis.timelineAnalysis.engagementCurve;
    const duration = currentAnalysis.videoDuration || videoDuration || 30;
    
    return curve.map((point: any, index: number) => {
      const timePercentage = (index / (curve.length - 1)) * 100;
      const intensity = point.engagement / 100;
      
      return {
        position: timePercentage,
        intensity,
        timestamp: (index / (curve.length - 1)) * duration
      };
    });
  };

  // Fullscreen toggle functionality
  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    
    if (!isVideoFullscreen) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsVideoFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Load projects when modal opens and check for existing creatives
  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      setProjectError(null);
      const userProjects = await ProjectService.getUserProjects();
      
      // Check for duplicate creatives in each project if we have current analysis data
      if (currentAnalysis?.creativeUrl) {
        const projectsWithDuplicateInfo = await Promise.all(
          userProjects.map(async (project) => {
            try {
              // Check if this creative URL already exists in this project
              const { data: existingCreatives, error } = await supabase
                .from('creatives')
                .select('id, name, created_at')
                .eq('project_id', project.id)
                .eq('url', currentAnalysis.creativeUrl);
              
              if (error) {
                console.warn(`Failed to check duplicates for project ${project.id}:`, error);
                return { ...project, hasExistingCreative: false, existingCreativeInfo: null };
              }
              
              const hasExisting = existingCreatives && existingCreatives.length > 0;
              return {
                ...project,
                hasExistingCreative: hasExisting,
                existingCreativeInfo: hasExisting ? existingCreatives[0] : null
              };
            } catch (err) {
              console.warn(`Error checking duplicates for project ${project.id}:`, err);
              return { ...project, hasExistingCreative: false, existingCreativeInfo: null };
            }
          })
        );
        
        setProjects(projectsWithDuplicateInfo);
      } else {
        setProjects(userProjects);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjectError(error instanceof Error ? error.message : 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Handle adding creative to project
  const handleAddToProject = async () => {
    if (!selectedProjectId || !currentAnalysis || !realAnalysisData) {
      setProjectError('Please select a project and ensure analysis data is available');
      return;
    }

    try {
      setAddingToProject(true);
      setProjectError(null);

      // Get the current user for ownership
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create creative record directly in database
      const creativeMetadata = {
        user_id: user.id,
        project_id: selectedProjectId,
        name: currentAnalysis.creativeName,
        type: currentAnalysis.creativeType,
        url: currentAnalysis.creativeUrl,
        file_size: 0, // Unknown for existing analyses
        file_type: currentAnalysis.creativeType === 'video' ? 'video/mp4' : 'image/jpeg',
        analysis_data: realAnalysisData as any, // Cast to Json type for database
        status: 'analyzed',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: creative, error: insertError } = await supabase
        .from('creatives')
        .insert(creativeMetadata)
        .select()
        .single();

      if (insertError) {
        throw new Error(`Failed to add creative to project: ${insertError.message}`);
      }
      
      // Log the creative upload activity
      try {
        await ActivityService.logCreativeUploaded(selectedProjectId, creative.id, creative.name);
      } catch (activityError) {
        // Don't fail the whole operation if activity logging fails
        console.warn('Failed to log creative upload activity:', activityError);
      }
      
      // Close modal and navigate to project
      handleCloseProjectModal();
      
      // Navigate to the project detail page
      navigate(`/dashboard/projects/${selectedProjectId}`);
      
    } catch (error) {
      console.error('Failed to add creative to project:', error);
      setProjectError(error instanceof Error ? error.message : 'Failed to add creative to project');
    } finally {
      setAddingToProject(false);
    }
  };

  // Handle project modal open
  const handleOpenProjectModal = () => {
    setShowProjectModal(true);
    loadProjects(); // Load projects when modal opens
  };

  // Handle project modal close
  const handleCloseProjectModal = () => {
    setShowProjectModal(false);
    setSelectedProjectId('');
    setProjectError(null);
  };

  // Helper function to reset all analysis-related state
  const resetAnalysisState = (resetCurrentAnalysisId = true) => {
    console.log('🔄 Resetting all analysis state...');
    setUploadProgress(0);
    setIsAnalyzing(false);
    setAutoProcessingVersion(false); // Reset auto-processing state
    setAnalysisError(null);
    if (resetCurrentAnalysisId) {
      setCurrentAnalysisId('');
    }
    setShowUpload(true);
    setRealAnalysisData(null);
    setLoadingRealData(false);
    setRealDataError(null);
    setEnhancedImageUrl(null);
    setShowEnhancedImage(false);
    setIsEditingName(false);
    setEditingName('');
    setRenamingLoading(false);
    setActiveTab('compliance');
    setShowVersionHistory(false);
    setShowCompareModal(false);
    setCompareVersionId(null);
    setSelectedVersions([]);
    setComparison(null);
    setShowComparison(false);
    setComparingVersions(false);
    setRestoringVersion(null);
    setShowRestoreConfirmModal(false);
    setVersionToRestore(null);
    setShowUndoNotification(false);
    if (undoTimeout) {
      clearTimeout(undoTimeout);
      setUndoTimeout(null);
    }
    setPreviousCurrentVersion(null);
    setShowNewVersionModal(false);
    setShowProjectModal(false);
    setShowAddCreativeModal(false);
    console.log('✅ All analysis state reset complete');
  };

  // Export handlers
  const handleExport = async (format: 'pdf' | 'csv' | 'json') => {
    if (!currentAnalysis) return;

    setExportLoading(true);
    setShowExportDropdown(false);

    try {
      // Transform currentAnalysis to match ExportService expected format
      const exportData = {
        id: currentAnalysis.id || 'unknown',
        creativeName: currentAnalysis.creativeName || 'Untitled Creative',
        creativeType: currentAnalysis.creativeType || 'image',
        creativeUrl: currentAnalysis.creativeUrl || '',
        analysisDate: currentAnalysis.analysisDate || new Date().toISOString(),
        overallScore: currentAnalysis.overallScore ?? 0,
        creditsUsed: currentAnalysis.creditsUsed ?? 1,
        version: currentAnalysis.version || '1.0',
        platforms: currentAnalysis.platforms || {},
        performanceAnalysis: currentAnalysis.performanceAnalysis || {
          overallPerformanceScore: currentAnalysis.overallScore ?? 0,
          brandAwarenessScore: currentAnalysis.overallScore ?? 0,
          keyFactors: {
            attention: { score: 0, description: 'No data available', details: [] },
            clarity: { score: 0, description: 'No data available', details: [] },
            engagement: { score: 0, description: 'No data available', details: [] }
          }
        },
        aiRecommendations: currentAnalysis.aiRecommendations || []
      };

      switch (format) {
        case 'pdf':
          await ExportService.exportToPDF(exportData);
          break;
        case 'csv':
          ExportService.exportToCSV(exportData);
          break;
        case 'json':
          ExportService.exportToJSON(exportData);
          break;
      }
    } catch (error) {
      console.error(`Error exporting ${format.toUpperCase()}:`, error);
      // You could add a toast notification here
    } finally {
      setExportLoading(false);
    }
  };

  // Share handlers
  const showShareNotification = (message: string) => {
    setShareNotification(message);
    setTimeout(() => setShareNotification(null), 3000);
  };

  const getSocialShareUrls = (analysisId: string) => {
    const shareUrl = `${window.location.origin}/dashboard/analysis?id=${analysisId}`;
    const creativeName = currentAnalysis?.creativeName || 'Creative';
    const score = currentAnalysis?.overallScore || 0;
    const creativeType = currentAnalysis?.creativeType || 'creative';
    const analysisDate = currentAnalysis?.analysisDate ? new Date(currentAnalysis.analysisDate).toLocaleDateString() : '';
    
    // Count total issues across platforms
    let totalIssues = 0;
    let criticalIssues = 0;
    let platformCount = 0;
    
    if (currentAnalysis?.platforms) {
      platformCount = Object.keys(currentAnalysis.platforms).length;
      Object.values(currentAnalysis.platforms).forEach((platform: any) => {
        if (platform.issues) {
          totalIssues += platform.issues.length;
          criticalIssues += platform.issues.filter((issue: any) => issue.severity === 'critical').length;
        }
      });
    }

    // Generate contextual message based on score and findings
    let contextMessage = '';
    let emoji = '';
    
    if (score >= 90) {
      contextMessage = `Excellent compliance! My ${creativeType} "${creativeName}" scored ${score}% across ${platformCount} platforms with minimal issues.`;
      emoji = '🎯✨';
    } else if (score >= 80) {
      contextMessage = `Strong performance! "${creativeName}" achieved ${score}% compliance score with ${totalIssues} areas for improvement across ${platformCount} platforms.`;
      emoji = '🚀💪';
    } else if (score >= 70) {
      contextMessage = `Good potential! "${creativeName}" scored ${score}% with ${totalIssues} optimization opportunities identified across ${platformCount} advertising platforms.`;
      emoji = '📈⚡';
    } else if (score >= 60) {
      contextMessage = `Work in progress: "${creativeName}" has ${score}% compliance with ${totalIssues} issues to address${criticalIssues > 0 ? ` (${criticalIssues} critical)` : ''} across ${platformCount} platforms.`;
      emoji = '🔧🎯';
    } else {
      contextMessage = `Major improvements needed: "${creativeName}" scored ${score}% with ${totalIssues} compliance issues${criticalIssues > 0 ? ` including ${criticalIssues} critical` : ''} across ${platformCount} platforms.`;
      emoji = '⚠️🛠️';
    }
    
    const aiInsight = currentAnalysis?.aiRecommendations?.length > 0 ? 
      ` AI provided ${currentAnalysis.aiRecommendations.length} actionable recommendations.` : '';
    
    const message = `${contextMessage}${aiInsight} ${emoji}\n\n🤖 Powered by AI creative analysis | Rulevia`;
    const hashtags = "marketing,advertising,AI,compliance,creativeanalysis,adtech";
    
    return {
      linkedin: {
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        message: message,
        instructions: 'LinkedIn will open - paste the copied text to share your analysis!'
      },
      twitter: {
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`,
        message: message,
        instructions: 'Twitter will pre-fill your tweet - just click Tweet!'
      },
      facebook: {
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        message: message,
        instructions: 'Facebook will open - paste the copied text to share your analysis!'
      },
      reddit: {
        url: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(message)}`,
        message: message,
        instructions: 'Reddit will pre-fill with your analysis details - just click Submit!'
      }
    };
  };

  const handleFileUpload = async (file: File) => {
    console.log('🚀 AnalysisPage: Starting file upload and analysis');
    console.log('📁 File details:', { name: file.name, size: file.size, type: file.type });
    
    // DEBUG: Check all relevant variables at the start of file upload
    console.log('🔍 DEBUG: File upload variables:', {
      originalCreativeForVersioning,
      contextProjectId,
      versionOfCreative,
      currentURL: window.location.href,
      urlParams: new URLSearchParams(window.location.search).toString()
    });
    
    // Enhanced safeguard: Prevent multiple simultaneous uploads/versions for the same original creative
    const isVersionCreation = originalCreativeForVersioning || new URLSearchParams(window.location.search).get('versionOfCreative');
    if (isVersionCreation) {
      // Early sessionStorage-based safeguard to prevent duplicate triggers
      const targetOriginalId = originalCreativeForVersioning || new URLSearchParams(window.location.search).get('versionOfCreative');
      const versionCreationKey = `creating_version_${targetOriginalId}`;
      const existingFlag = sessionStorage.getItem(versionCreationKey);
      
      if (existingFlag) {
        const flagTime = parseInt(existingFlag);
        const twoMinutesAgo = Date.now() - (2 * 60 * 1000); // Reduced from 5 minutes to 2 minutes
        
        if (flagTime >= twoMinutesAgo) {
          console.warn('⚠️ Version creation already in progress for creative:', targetOriginalId);
          console.warn('⚠️ Skipping duplicate request (early safeguard)');
          return;
        } else {
          console.log('🧹 Clearing stale version creation flag (early check)');
          sessionStorage.removeItem(versionCreationKey);
        }
      }
      
      // Set the flag early to prevent race conditions
      sessionStorage.setItem(versionCreationKey, Date.now().toString());
      console.log('🚦 Set version creation flag:', versionCreationKey);
      
      // Also check the isAnalyzing state
      if (isAnalyzing) {
        console.warn('⚠️ Analysis already in progress, skipping duplicate request');
        sessionStorage.removeItem(versionCreationKey); // Clean up flag
        return;
      }
    }
    
    // Check if user can perform analysis before proceeding
    if (!user?.id) {
      setAnalysisError('User authentication required');
      return;
    }

    console.log('🔍 Checking analysis limits...');
    const limitCheck = await LimitEnforcementService.canPerformAnalysis(user.id);
    
    if (!limitCheck.allowed) {
      console.log('❌ Analysis limit exceeded:', limitCheck.reason);
      setAnalysisError(limitCheck.reason || 'Analysis limit exceeded');
      return;
    }
    
    console.log('✅ Analysis limits check passed');
    
    // Reset ALL analysis-related state for a fresh start
    resetAnalysisState();
    
    console.log('🔄 All state reset for new analysis');
    
    try {
      // Prevent duplicate uploads by checking if one is already in progress
      if (uploadProgress > 0 && uploadProgress < 100) {
        console.warn('⚠️ Upload already in progress, skipping duplicate upload');
        return;
      }
      
      // Show immediate feedback that upload is starting
      setUploadProgress(1);
      console.log('📊 AnalysisPage: Upload progress set to 1%');
      
      // Track analysis start
      GTMService.trackAnalysisStart(
        file.type.includes('image') ? 'image' : 'video',
        file.type,
        file.size,
        [], // platforms will be available later
        user.id,
        contextProjectId || undefined
      );
      
      // Get URL parameters for debugging and version notes
      const urlParams = new URLSearchParams(window.location.search);
      
      // Determine if we should auto-assign to project or create version
      const shouldAutoAssign = contextProjectId && contextProjectId.trim() !== '';
      
      // Check both the state and URL parameters for version creation
      const urlVersionParam = urlParams.get('versionOfCreative');
      const isCreatingVersion = (originalCreativeForVersioning && originalCreativeForVersioning.trim() !== '') || 
                               (urlVersionParam && urlVersionParam.trim() !== '');
      
      // Update originalCreativeForVersioning if it's missing but URL param exists
      if (!originalCreativeForVersioning && urlVersionParam) {
        console.log('🔄 Setting originalCreativeForVersioning from URL parameter:', urlVersionParam);
        setOriginalCreativeForVersioning(urlVersionParam);
      }
      
      console.log('📊 Upload context:', {
        contextProjectId,
        shouldAutoAssign,
        originalCreativeForVersioning,
        isCreatingVersion,
        versionOfCreative: urlParams.get('versionOfCreative'),
        urlParamsString: urlParams.toString()
      });
      
      console.log('🔍 DEBUG: Version creation conditions:', {
        'originalCreativeForVersioning value': originalCreativeForVersioning,
        'originalCreativeForVersioning type': typeof originalCreativeForVersioning,
        'originalCreativeForVersioning.trim()': originalCreativeForVersioning ? originalCreativeForVersioning.trim() : null,
        'isCreatingVersion': isCreatingVersion,
        'urlParams.get versionOfCreative': urlParams.get('versionOfCreative'),
        'current URL': window.location.href
      });
      
      if (isCreatingVersion) {
        console.log(`🔄 AnalysisPage: Creating new version of creative ${originalCreativeForVersioning}...`);
      } else {
        console.log(`🏗️ AnalysisPage: Creating creative ${shouldAutoAssign ? 'with auto-assignment to project ' + contextProjectId : 'for manual project assignment'}...`);
      }
      
      let creativeResult;
      let timeoutOccurred = false;
      
      // Set up fallback polling mechanism
      const startTime = Date.now();
      const pollForCompletion = async () => {
        // Poll every 5 seconds for completed analyses after 2 minutes
        const interval = setInterval(async () => {
          try {
            if (Date.now() - startTime < 120000) return; // Wait 2 minutes before polling
            
            console.log('🔍 Polling for completed analysis...');
            const { data: recentAnalyses, error } = await supabase
              .from('analyses')
              .select('id, created_at, status, file_name')
              .gte('created_at', new Date(startTime).toISOString())
              .eq('status', 'completed')
              .order('created_at', { ascending: false })
              .limit(1);

            if (error) {
              console.error('❌ Polling error:', error);
              return;
            }

            if (recentAnalyses && recentAnalyses.length > 0) {
              const analysis = recentAnalyses[0];
              console.log('✅ Found completed analysis via polling:', analysis.id);
              clearInterval(interval);
              
                      // Navigate to analysis detail
        navigate(`/dashboard/analysis?id=${analysis.id}`);
            }
          } catch (pollError) {
            console.error('❌ Polling error:', pollError);
          }
        }, 5000);

        // Stop polling after 15 minutes total
        setTimeout(() => {
          clearInterval(interval);
          if (timeoutOccurred) {
            console.log('⏰ Stopping fallback polling after 15 minutes');
          }
        }, 900000);
      };

      try {
        // Add timeout wrapper to prevent infinite hanging (10 minutes - increased from 5)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Upload and analysis timeout after 10 minutes')), 10 * 60 * 1000)
        );
        
        let createPromise;
        
        const progressCallback = (progress: any) => {
          console.log('🔄 AnalysisPage: Progress update received:', progress);
          switch (progress.stage) {
            case 'uploading':
              console.log(`📤 Upload progress: ${progress.progress}%`);
              setUploadProgress(progress.progress || 0);
              setIsAnalyzing(false);
              break;
            case 'saving':
              console.log(`💾 Saving progress: ${progress.progress}%`);
              setUploadProgress(progress.progress || 30);
              setIsAnalyzing(false);
              break;
            case 'analyzing':
              console.log(`🤖 Analysis progress: ${progress.progress}%`);
              // Ensure upload shows as complete before analysis starts
              if (progress.progress >= 45) {
                setUploadProgress(100); // Upload is complete
                setIsAnalyzing(true); // Now show analysis state
                setAutoProcessingVersion(false); // Switch from auto-processing to normal analysis UI
              } else {
                setUploadProgress(progress.progress || 45);
                setIsAnalyzing(false); // Still uploading
              }
              break;
            case 'completed':
              console.log('✅ Process completed');
              setUploadProgress(100);
              setIsAnalyzing(false);
              setAutoProcessingVersion(false); // Reset auto-processing state
              break;
          }
        };

        if (isCreatingVersion) {
          // For version creation, we need to ensure we inherit the original creative's project
          // First, fetch the original creative's project_id if not already set
          if (!contextProjectId && originalCreativeForVersioning) {
            console.log('🔍 Fetching original creative project context during upload...');
            try {
              const { data: originalCreative, error } = await supabase
                .from('creatives')
                .select('project_id')
                .eq('id', originalCreativeForVersioning)
                .single();
                
              if (error) {
                console.error('❌ Failed to fetch original creative during upload:', error);
              } else if (originalCreative?.project_id) {
                console.log('🏗️ Setting project context during upload:', originalCreative.project_id);
                setContextProjectId(originalCreative.project_id);
                // Use the project_id immediately for this upload
                createPromise = CreativeService.createCreative(
                  {
                    projectId: originalCreative.project_id,
                    file: file,
                    name: file.name,
                    autoAnalyze: true
                  },
                  progressCallback
                );
              } else {
                console.log('ℹ️ Original creative has no project_id - creating standalone version');
                createPromise = CreativeService.createCreative(
                  {
                    projectId: undefined,
                    file: file,
                    name: file.name,
                    autoAnalyze: true
                  },
                  progressCallback
                );
              }
            } catch (error) {
              console.error('❌ Error fetching original creative during upload:', error);
              // Fallback to regular creation
              createPromise = CreativeService.createCreative(
                {
                  projectId: shouldAutoAssign ? contextProjectId : undefined,
                  file: file,
                  name: file.name,
                  autoAnalyze: true
                },
                progressCallback
              );
            }
          } else {
            // Use existing contextProjectId
            createPromise = CreativeService.createCreative(
              {
                projectId: shouldAutoAssign ? contextProjectId : undefined,
                file: file,
                name: file.name,
                autoAnalyze: true
              },
              progressCallback
            );
          }
        } else {
          // Create new creative
          createPromise = CreativeService.createCreative(
            {
              projectId: shouldAutoAssign ? contextProjectId : undefined, // Auto-assign if in project context
              file: file,
              name: file.name,
              autoAnalyze: true
            },
            progressCallback
          );
        }
        
        console.log('⏳ AnalysisPage: Waiting for CreativeService.createCreative to complete...');
        
        // Race between the create promise and timeout
        creativeResult = await Promise.race([createPromise, timeoutPromise]) as any;
        console.log('✅ AnalysisPage: CreativeService.createCreative completed successfully');
        console.log('📊 CreativeResult received:', {
          id: creativeResult?.id,
          hasAnalysis: !!creativeResult?.analysis,
          analysisId: creativeResult?.analysis?.analysisId
        });

        // Handle version creation if needed
        console.log('🔍 DEBUG: Checking version creation conditions after upload:', {
          isCreatingVersion,
          'creativeResult?.id': creativeResult?.id,
          'creativeResult': creativeResult ? 'exists' : 'null',
          'will execute version creation': isCreatingVersion && creativeResult?.id
        });
        
        if (isCreatingVersion && creativeResult?.id) {
          try {
            // Use the URL parameter if state is empty
            const targetOriginalId = originalCreativeForVersioning || urlVersionParam;
            
            // We already have safeguards at the beginning of handleFileUpload, 
            // so we don't need to check again here - just proceed with version creation
            console.log('🔄 Proceeding with version creation for:', targetOriginalId);
            
            console.log('🔄 Converting creative to version of original:', targetOriginalId);
            console.log('🔄 Version creation context:', {
              isCreatingVersion,
              originalCreativeForVersioning,
              urlVersionParam,
              targetOriginalId,
              creativeResultId: creativeResult.id,
              hasAnalysis: !!creativeResult.analysis
            });
            console.log('📊 Creative result data:', {
              id: creativeResult.id,
              url: creativeResult.url,
              name: creativeResult.name,
              type: creativeResult.type,
              file_size: creativeResult.file_size,
              file_type: creativeResult.file_type
            });
            
            // Create new version using the uploaded creative
            const versionRequest: CreateVersionRequest = {
              originalCreativeId: targetOriginalId,
              newFileUrl: creativeResult.url,
              name: creativeResult.name,
              versionName: undefined, // Don't set a version name - version number is sufficient
              versionNotes: urlParams.get('versionNotes') || 'New version created from analysis page',
              fileSize: creativeResult.file_size || undefined,
              fileType: creativeResult.file_type || undefined,
              type: creativeResult.type
            };
            
            console.log('📋 Version request data:', versionRequest);
            
            // Wait for analysis to be fully saved and verify it exists before version creation
            if (creativeResult.analysis?.analysisId) {
              console.log('⏳ Waiting for analysis to be fully saved...');
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // Verify analysis exists before proceeding
              const { data: analysisExists } = await supabase
                .from('analyses')
                .select('id')
                .eq('id', creativeResult.analysis.analysisId)
                .single();
                
              if (!analysisExists) {
                console.warn('⚠️ Analysis not found immediately, waiting longer...');
                await new Promise(resolve => setTimeout(resolve, 2000));
              } else {
                console.log('✅ Analysis verified, proceeding with version creation');
              }
            }
            
            console.log('🚀 Calling VersionService.createNewVersion...');
            console.log('📊 Analysis data being passed:', {
              hasAnalysis: !!creativeResult.analysis,
              analysisId: creativeResult.analysis?.analysisId,
              overallScore: creativeResult.analysis?.overallScore
            });
            const newVersion = await VersionService.createNewVersion(versionRequest, creativeResult.analysis, creativeResult.url);
            console.log('✅ Version created successfully:', {
              id: newVersion.id,
              versionNumber: newVersion.versionNumber,
              familyName: newVersion.familyName,
              parentId: newVersion.parentCreativeId
            });
            
            // Verify analysis was properly transferred to the new version
            console.log('🔍 Verifying analysis transfer to new version...');
            let analysisTransferConfirmed = false;
            for (let i = 0; i < 5; i++) {
              const { data: versionAnalysis } = await supabase
                .from('analyses')
                .select('id')
                .eq('creative_id', newVersion.id)
                .single();
                
              if (versionAnalysis) {
                console.log('✅ Analysis found for new version:', versionAnalysis.id);
                analysisTransferConfirmed = true;
                break;
              }
              console.log(`⏳ Analysis not found for new version, retry ${i + 1}/5`);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            if (analysisTransferConfirmed) {
              // Only delete temporary creative AFTER confirming analysis is transferred
              console.log('🗑️ Deleting temporary creative after confirming analysis transfer:', creativeResult.id);
              try {
                const { error: deleteError } = await supabase
                  .from('creatives')
                  .delete()
                  .eq('id', creativeResult.id);
                  
                if (deleteError) {
                  console.error('⚠️ Failed to delete temporary creative:', deleteError);
                } else {
                  console.log('✅ Temporary creative deleted successfully');
                }
              } catch (deleteError) {
                console.error('⚠️ Error deleting temporary creative:', deleteError);
              }
            } else {
              console.error('❌ Analysis not transferred to new version, keeping temporary creative');
              // If analysis transfer failed, still try to navigate to the original analysis
              if (creativeResult.analysis?.analysisId) {
                console.log('🔄 Fallback: navigating to original analysis:', creativeResult.analysis.analysisId);
                navigate(`/dashboard/analysis?id=${creativeResult.analysis.analysisId}`, { replace: true });
                return;
              }
            }
            
            console.log('✅ Successfully converted to version');
            
            // Clear the version creation flag (set at the beginning of handleFileUpload)
            const versionCreationKey = `creating_version_${targetOriginalId}`;
            sessionStorage.removeItem(versionCreationKey);
            
            // Navigate to new version's analysis (we already confirmed it exists)
            if (analysisTransferConfirmed) {
              const { data: newVersionAnalysis } = await supabase
                .from('analyses')
                .select('id')
                .eq('creative_id', newVersion.id)
                .single();
              
              if (newVersionAnalysis) {
                console.log('✅ Navigating to new version analysis:', newVersionAnalysis.id);
                navigate(`/dashboard/analysis?id=${newVersionAnalysis.id}`, { replace: true });
                return;
              }
            }
            
            // Fallback: navigate to recent analyses if something went wrong
            console.warn('⚠️ Could not find new version analysis, navigating to recent analyses');
            navigate('/dashboard/recent-analyses', { replace: true });
            return;
            
          } catch (versionError) {
            // Clear the version creation flag on error
            const versionCreationKey = `creating_version_${targetOriginalId}`;
            sessionStorage.removeItem(versionCreationKey);
            
            console.error('❌ Failed to convert to version:', versionError);
            console.error('❌ Version error details:', {
              message: versionError instanceof Error ? versionError.message : String(versionError),
              stack: versionError instanceof Error ? versionError.stack : undefined,
              originalCreativeForVersioning,
              creativeResultId: creativeResult?.id
            });
            
            // Clean up the temporary creative since version creation failed
            try {
              await supabase
                .from('creatives')
                .delete()
                .eq('id', creativeResult.id);
              console.log('🗑️ Cleaned up temporary creative after version creation failure');
            } catch (cleanupError) {
              console.error('❌ Failed to clean up temporary creative:', cleanupError);
            }
            
            // Show error to user and stop the process
            setAnalysisError(`Failed to create version: ${versionError instanceof Error ? versionError.message : 'Unknown error'}`);
            setIsAnalyzing(false);
            setUploadProgress(0);
            return; // Don't continue with normal flow
          }
        }

        // Credits are automatically deducted by the API route during analysis
        // Broadcast credit change event to update UI immediately
        if (user?.id && creativeResult?.analysis) {
          console.log('📢 Broadcasting credit change event for immediate UI update');
          const { broadcastCreditChange } = await import('../lib/credit-events');
          
          // Get the updated credit info from the analysis result
          // The API should return analysesUsed and analysesLimit
          const analysesUsed = creativeResult.analysesUsed || 0;
          const analysesLimit = creativeResult.analysesLimit || 0;
          const newBalance = Math.max(0, analysesLimit - analysesUsed);
          
          console.log('💳 Credit info from API response:', {
            analysesUsed,
            analysesLimit, 
            newBalance
          });
          
          // Broadcast that 1 analysis credit was consumed with the actual new balance
          broadcastCreditChange(
            'credit_consumed',
            user.id,
            -1, // 1 credit consumed
            newBalance, // Use the actual new balance from API
            {
              analysisId: creativeResult.analysis.analysisId,
              reason: 'analysis_completed',
              analysesUsed,
              analysesLimit
            }
          );
        }
        console.log('✅ Analysis completed successfully - credits already deducted by API');

        // Analysis completed successfully - show success notification
        console.log('✅ Analysis completed successfully!');
        
        // Navigate after successful completion - back to project if in project context
        if (creativeResult?.analysis?.analysisId) {
          console.log('🎯 AnalysisPage: Navigating to analysis detail:', creativeResult.analysis.analysisId);
          
          // Trigger real-time update by dispatching custom event
          window.dispatchEvent(new CustomEvent('analysisCompleted', {
            detail: { analysisId: creativeResult.analysis.analysisId }
          }));
          
          // Track analysis completion
          GTMService.trackAnalysisComplete(
            file.type.includes('image') ? 'image' : 'video',
            file.type,
            [], // platforms will be available in analysis data
            user.id,
            contextProjectId || undefined
          );
          
          if (isCreatingVersion && contextProjectId) {
            // If creating version, navigate back to project detail page with version history modal
            console.log('🏠 Returning to project detail page with version history modal after version creation:', contextProjectId);
            navigate(`/dashboard/projects/${contextProjectId}?showVersionHistory=${originalCreativeForVersioning}`);
          } else if (isCreatingVersion && !contextProjectId) {
            // For individual creative versioning, navigate to the new analysis
            console.log('🏠 Navigating to new analysis for individual creative versioning');
            navigate(`/dashboard/analysis?id=${creativeResult.analysis.analysisId}`);
          } else if (shouldAutoAssign && contextProjectId) {
            // If auto-assigned to project, navigate back to project detail page
            console.log('🏠 Returning to project detail page:', contextProjectId);
            navigate(`/dashboard/projects/${contextProjectId}`);
          } else {
            // Regular flow - go to analysis detail
            navigate(`/dashboard/analysis?id=${creativeResult.analysis.analysisId}`);
          }
          return; // Exit early since we're navigating away
        } else {
          console.error('❌ No analysis ID found in creative result');
          setAnalysisError('Analysis completed but no analysis ID found. Please try again.');
          setIsAnalyzing(false);
          setUploadProgress(0);
          return;
        }
        
      } catch (creativeServiceError) {
        console.error('❌ CreativeService.createCreative failed:', creativeServiceError);
        console.error('❌ Error type:', typeof creativeServiceError);
        console.error('❌ Error message:', creativeServiceError instanceof Error ? creativeServiceError.message : String(creativeServiceError));
        
        // Check if it's a timeout
        if (creativeServiceError instanceof Error && creativeServiceError.message.includes('timeout')) {
          console.log('⏰ Timeout occurred, starting fallback polling...');
          timeoutOccurred = true;
          pollForCompletion();
          
          setUploadProgress(50);
          
          // Show user that analysis is continuing in background
          setAnalysisError('Analysis is taking longer than expected. We\'ll continue processing in the background and redirect you when complete.');
          setIsAnalyzing(false);
          return;
        }
        
        throw creativeServiceError;
      }
    } catch (error) {
      console.error('File upload and analysis error:', error);
      
      // Track analysis error
      GTMService.trackAnalysisError(
        file.type.includes('image') ? 'image' : 'video',
        error instanceof Error ? error.message : 'Upload failed',
        user.id,
        contextProjectId || undefined
      );
      
      setAnalysisError(error instanceof Error ? error.message : 'Upload failed');
      setIsAnalyzing(false);
      setUploadProgress(0);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drag area completely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      // Validate file type
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        handleFileUpload(file);
      } else {
        alert('Please upload an image or video file.');
      }
    }
  };

  // Version drag and drop handlers
  const handleVersionDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVersionDragging(true);
  };

  const handleVersionDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drag area completely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsVersionDragging(false);
    }
  };

  const handleVersionDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleVersionDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVersionDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      // Validate file type
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        setVersionFile(file);
      } else {
        alert('Please upload an image or video file.');
      }
    }
  };

  // Show upload page if no analysis is selected
  if (showUpload) {
    return (
      <div className="min-h-screen overflow-x-hidden">
        <SEOHead {...getPageSEO('/analysis')} />
        <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="hover:text-purple-600 transition-colors flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
                <span>/</span>
                <span>Analysis</span>
              </div>
              {currentAnalysisId && (
                <button
                  onClick={() => {
                    console.log('🔄 New Analysis button clicked - resetting state');
                    resetAnalysisState();
                    navigate('/dashboard/analysis', { replace: true });
                  }}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2 text-sm sm:text-base"
                >
                  <Upload className="w-4 h-4" />
                  <span>New Analysis</span>
                </button>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              {originalCreativeForVersioning ? 'Create New Version' : 'Creative Analysis'}
            </h1>
            <p className="text-gray-600">
              {originalCreativeForVersioning 
                ? 'Upload a new version of your creative to track changes and improvements over time.'
                : 'Upload your creative to get instant compliance analysis and performance predictions across all major advertising platforms.'
              }
            </p>
            {originalCreativeForVersioning && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2 text-purple-700">
                  <GitBranch className="w-4 h-4" />
                  <span className="text-sm font-medium">Creating new version of existing creative</span>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Upload Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8"
        >
          {!isAnalyzing && uploadProgress === 0 && !autoProcessingVersion && (
            <div className="text-center">
              <div 
                className={`border-2 border-dashed rounded-xl p-12 transition-colors ${
                  isDragging 
                    ? 'border-purple-400 bg-purple-50' 
                    : 'border-gray-300 hover:border-purple-400'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {originalCreativeForVersioning ? 'Upload New Version' : 'Upload Your Creative'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {originalCreativeForVersioning 
                    ? 'Upload the new version of your creative file here'
                    : 'Drag and drop your image or video file here, or click to browse'
                  }
                </p>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                  }}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all cursor-pointer inline-block"
                >
                  Choose File
                </label>
                <p className="text-sm text-gray-500 mt-4">
                  Supports images (JPG, PNG, GIF, WebP, HEIC, HEIF) up to 20MB, videos (MP4, MOV, AVI, WebM, etc.) up to 1GB
                </p>
              </div>
            </div>
          )}

          {autoProcessingVersion && (
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600"></div>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Processing New Version</h3>
              <p className="text-gray-600 mb-4">
                {uploadProgress > 0 
                  ? `Uploading and analyzing your new version... ${uploadProgress}% complete`
                  : 'Loading your uploaded file and starting analysis...'
                }
              </p>
              {uploadProgress > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4 max-w-sm mx-auto">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
              <div className="mt-6 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2 text-purple-700">
                  <GitBranch className="w-4 h-4" />
                  <span className="text-sm">Your file will be automatically processed and analyzed</span>
                </div>
              </div>
            </div>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && !autoProcessingVersion && (
            <div className="text-center">
              <Upload className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Uploading...</h3>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-gray-600">{uploadProgress}% complete</p>
            </div>
          )}

          {isAnalyzing && (
            <div>
              <AnalysisLoadingScreen
                message="Running compliance checks and performance analysis with real AI."
                title="Analyzing Creative..."
              />
              <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4 mt-4">
                {Object.entries(platformInfo).map(([key, platform]) => (
                  <div key={key} className="flex flex-col items-center gap-2">
                    <PlatformIcon platform={key} size="lg" />
                    <span className="text-xs text-gray-600">{platform.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {analysisError && (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Analysis Failed</h3>
              <p className="text-red-600 mb-6">{analysisError}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {analysisError.toLowerCase().includes('credit') && (
                  <button
                    onClick={() => {
                      navigate('/dashboard/credits', { state: { tab: 'purchase' } });
                    }}
                    className="bg-green-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
                  >
                    Purchase Credits
                  </button>
                )}
                <button
                  onClick={() => {
                    setAnalysisError(null);
                    setUploadProgress(0);
                    setIsAnalyzing(false);
                  }}
                  className="bg-purple-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Recent Analyses Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8"
        >
          <RecentAnalysesWidget 
            onViewAll={() => navigate('/dashboard/recent-analyses')}
            onHistoryClick={(analysis) => {
              if (analysis.id) {
                handleRecentAnalysisClick(analysis.id);
              }
            }}
          />
        </motion.div>
        </div>
      </div>
    );
  }

  // Show "Add Creative" modal when coming from project context
  if (showAddCreativeModal && contextProjectId) {
    return (
      <div className="min-h-screen overflow-x-hidden">
        <SEOHead {...getPageSEO('/analysis')} />
        <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <button
                onClick={() => navigate(`/dashboard/projects/${contextProjectId}`)}
                className="hover:text-purple-600 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Project</span>
              </button>
              <span>/</span>
              <span>Add Creative</span>
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Add Creative to Project</h1>
            <p className="text-gray-600">
              Choose to add an analyzed creative from your history or create a new analysis.
            </p>
          </motion.div>
        </div>

        {/* Add Creative Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"
        >
          {/* Add from History */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8 hover:border-purple-300 transition-colors">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Archive className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Add from History</h3>
              <p className="text-gray-600 mb-6">
                Select a previously analyzed creative from your history to add to this project.
              </p>
              <button
                onClick={() => {
                  // Navigate to recent analyses with project context
                  navigate(`/dashboard/recent-analyses?projectId=${contextProjectId}&mode=select`);
                }}
                className="w-full bg-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Browse History
              </button>
            </div>
          </div>

          {/* Create New Analysis */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8 hover:border-purple-300 transition-colors">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Create New Analysis</h3>
              <p className="text-gray-600 mb-6">
                Upload a new creative file and run analysis. It will be automatically added to this project.
              </p>
              <button
                onClick={() => {
                  setShowAddCreativeModal(false);
                  setShowUpload(true);
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Upload & Analyze
              </button>
            </div>
          </div>
        </motion.div>

        {/* Recent Analyses Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-8"
        >
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Add from Recent</h3>
            <RecentAnalysesWidget 
              onViewAll={() => navigate(`/dashboard/recent-analyses?projectId=${contextProjectId}&mode=select`)}
              onHistoryClick={(analysis) => {
                if (analysis.id) {
                                     // Add this creative to the project directly
                   const addToProjectDirectly = async () => {
                     try {
                       // Find the creative by analysis ID and update its project assignment
                       const { data: creatives, error: findError } = await supabase
                         .from('creatives')
                         .select('*')
                         .contains('analysis_data', { analysisId: analysis.id })
                         .limit(1);
                       
                       if (findError || !creatives || creatives.length === 0) {
                         console.error('Creative not found for analysis:', analysis.id);
                         return;
                       }
                       
                       const creative = creatives[0];
                       
                       // Update the creative to assign it to the project
                       const { error: updateError } = await supabase
                         .from('creatives')
                         .update({ 
                           project_id: contextProjectId,
                           status: 'uploaded' 
                         })
                         .eq('id', creative.id);
                       
                       if (updateError) {
                         console.error('Failed to assign creative to project:', updateError);
                         return;
                       }
                       
                       // Log the creative upload activity
                       try {
                         await ActivityService.logCreativeUploaded(contextProjectId, creative.id, creative.name);
                       } catch (activityError) {
                         // Don't fail the whole operation if activity logging fails
                         console.warn('Failed to log creative upload activity:', activityError);
                       }
                       
                       navigate(`/dashboard/projects/${contextProjectId}`);
                     } catch (error) {
                       console.error('Failed to add creative to project:', error);
                     }
                   };
                  addToProjectDirectly();
                }
              }}
            />
          </div>
        </motion.div>
        </div>
      </div>
    );
  }

  // Show loading state when fetching real data
  if (loadingRealData) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <SEOHead {...getPageSEO('/analysis')} />
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Analysis...</h2>
          <p className="text-gray-600">Fetching analysis data from database.</p>
        </div>
      </div>
    );
  }

  // Show error state when real data fails to load
  if (realDataError) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <SEOHead {...getPageSEO('/analysis')} />
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Analysis</h2>
          <p className="text-gray-600 mb-4">{realDataError}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!currentAnalysis) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <SEOHead {...getPageSEO('/analysis')} />
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Analysis Not Found</h2>
          <p className="text-gray-600">The requested analysis could not be found.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEOHead {...getPageSEO('/analysis')} />
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="hover:text-purple-600 transition-colors flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
                <span>/</span>
                <span>Analysis</span>
                <span>/</span>
                <span>{currentAnalysis.version}</span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleKeyPress}
                      className="text-3xl font-bold text-gray-900 bg-transparent border-b-2 border-purple-300 focus:border-purple-500 focus:outline-none"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveRename}
                      disabled={renamingLoading}
                      className="p-1 text-green-600 hover:text-green-700 transition-colors"
                      title="Save"
                    >
                      {renamingLoading ? (
                        <div className="w-5 h-5 border-2 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
                      ) : (
                        <Check className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={handleCancelRename}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Cancel"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <h1 className="text-3xl font-bold text-gray-900" title={currentAnalysis.creativeName}>
                      {truncateAnalysisName.title(currentAnalysis.creativeName)}
                    </h1>
                    <button
                      onClick={handleStartRename}
                      className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                      title="Rename creative"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-gray-600">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(currentAnalysis.analysisDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Version {currentAnalysis.version}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <CreditCard className="w-4 h-4" />
                  <span>{currentAnalysis.creditsUsed} credit used</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={() => setShowVersionHistory(!showVersionHistory)}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <Clock className="w-4 h-4" />
                <span>Version History</span>
                {showVersionHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowNewVersionModal(true)}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <GitBranch className="w-4 h-4" />
                <span>New Version</span>
              </button>
              <button
                onClick={handleOpenProjectModal}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <FolderPlus className="w-4 h-4" />
                <span>Add to Project</span>
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  disabled={exportLoading}
                  className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  {exportLoading ? (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span>{exportLoading ? 'Exporting...' : 'Export'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Export Dropdown */}
                {showExportDropdown && (
                  <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {ExportService.getExportOptions().map((option) => {
                      // Map icon names to components
                      const IconComponent = option.icon === 'FileText' ? FileText : 
                                           option.icon === 'BarChart3' ? BarChart3 : 
                                           option.icon === 'Code' ? Code : FileText;
                      
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleExport(option.id as 'pdf' | 'csv' | 'json')}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <IconComponent className="w-5 h-5 text-gray-600" />
                            <div>
                              <div className="font-medium text-gray-900">{option.label}</div>
                              <div className="text-sm text-gray-600">{option.description}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Re-analyze Progress Display */}
      {isReanalyzing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 text-purple-800 px-4 py-4 rounded-lg mb-6"
        >
          <div className="flex items-center space-x-3 mb-3">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
            <span className="font-medium">Re-analyzing Creative</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span>{reanalyzeMessage}</span>
              <span className="font-medium">{reanalyzeProgress}%</span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${reanalyzeProgress}%` }}
              ></div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Re-analyze Error Display */}
      {reanalyzeError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5" />
            <span>{reanalyzeError}</span>
          </div>
          <button
            onClick={() => setReanalyzeError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Version History Panel */}
      <AnimatePresence>
        {showVersionHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl border border-gray-200 p-6 mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Info className="w-4 h-4" />
                <span>Select up to 2 versions to compare</span>
              </div>
            </div>
            
            {/* Comparison Instructions */}
            {selectedVersions.length === 0 && versionHistory.length > 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Compare Versions</p>
                    <p>Select checkboxes on any two versions to compare their analysis results side-by-side, or click "Restore" to make a previous version current.</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Comparison Controls */}
            {selectedVersions.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <div className="text-sm text-blue-800">
                  {selectedVersions.length === 1 ? 
                    'Select one more version to compare' : 
                    'Two versions selected for comparison'
                  }
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedVersions([])}
                    className="text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    Clear Selection
                  </button>
                  {selectedVersions.length === 2 && (
                    <button
                      onClick={handleCompareVersions}
                      disabled={comparingVersions}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                    >
                      {comparingVersions && (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      )}
                      <span>{comparingVersions ? 'Comparing...' : 'Compare'}</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {loadingVersionHistory ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto mb-2" />
                  <p className="text-gray-600">Loading version history...</p>
                </div>
              ) : versionHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Archive className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No version history available</p>
                </div>
              ) : versionHistory.length === 1 ? (
                <div>
                  {versionHistory.map((version) => {
                    const isSelected = selectedVersions.includes(version.id);
                    return (
                      <div
                        key={version.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          version.id === currentAnalysisId ? 'border-purple-200 bg-purple-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4"></div> {/* Spacer where checkbox would be */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className="font-medium text-gray-900">{version.version}</span>
                              {version.status === 'current' && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                  Current
                                </span>
                              )}
                              <span className="text-sm text-gray-600">{version.author}</span>
                              <span className="text-sm text-gray-500">
                                {new Date(version.date).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{version.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                          <span className="text-base sm:text-lg font-bold text-gray-900">{version.score}%</span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHistoryClick(version.id);
                              }}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="mt-3 text-center py-4 text-sm text-gray-500">
                    <GitBranch className="w-4 h-4 inline-block mr-1" />
                    Create more versions to enable comparison feature
                  </div>
                </div>
              ) : versionHistory.map((version) => {
                const isSelected = selectedVersions.includes(version.id);
                return (
                  <div
                    key={version.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      isSelected ? 'border-blue-300 bg-blue-50' : 
                      version.id === currentAnalysisId ? 'border-purple-200 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleVersionSelection(version.id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                        onClick={(e) => e.stopPropagation()}
                        title="Select for comparison"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-900">{version.version}</span>
                          {version.status === 'current' && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                              Current
                            </span>
                          )}
                          <span className="text-sm text-gray-600">{version.author}</span>
                          <span className="text-sm text-gray-500">
                            {new Date(version.date).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{version.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-base sm:text-lg font-bold text-gray-900">{version.score}%</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleHistoryClick(version.id);
                          }}
                          className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          View
                        </button>
                        {version.status !== 'current' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetCurrentVersion(version);
                            }}
                            disabled={restoringVersion === version.id}
                            className="text-green-600 hover:text-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                          >
                            {restoringVersion === version.id && (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            )}
                            <span>{restoringVersion === version.id ? 'Restoring...' : 'Restore'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`grid gap-4 sm:gap-6 lg:gap-8 ${currentAnalysis.creativeType === 'video' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {/* Creative Preview */}
        <div className={currentAnalysis.creativeType === 'video' ? 'col-span-1' : 'lg:col-span-1'}>
          <motion.div
            ref={videoPreviewRef}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 sticky top-6"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Creative Preview</h2>
              <div className="flex items-center space-x-2">
                {currentAnalysis.creativeType === 'video' ? (
                  <FileVideo className="w-5 h-5 text-purple-600" />
                ) : (
                  <FileImage className="w-5 h-5 text-blue-600" />
                )}
                <span className="text-sm text-gray-600 capitalize">{currentAnalysis.creativeType}</span>
              </div>
            </div>
            
            <div className="relative rounded-lg overflow-hidden bg-gray-100 mb-4">
              {currentAnalysis.creativeType === 'video' ? (
                <div className="relative group">
                  {/* Video Loading State */}
                  {videoLoading && (
                    <div className="absolute inset-0 bg-black/80 rounded-lg flex items-center justify-center z-10">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                        <p className="text-white">Loading video...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Video Error State */}
                  {videoError && (
                    <div className="absolute inset-0 bg-black/80 rounded-lg flex items-center justify-center z-10">
                      <div className="text-center text-white p-4">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                        <p className="mb-4">{videoError}</p>
                        <button
                          onClick={() => {
                            setVideoError(null);
                            handleVideoLoadError();
                          }}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  )}

                  {(videoStreamUrl || secureCreativeUrl || currentAnalysis?.creativeUrl) && (
                    <video
                      ref={videoRef}
                      src={videoStreamUrl || secureCreativeUrl || currentAnalysis?.creativeUrl}
                      crossOrigin="anonymous"
                      className={`w-full ${
                        videoMetadata.aspectRatio
                          ? 'h-auto'
                          : 'min-h-[300px] md:min-h-[400px] lg:min-h-[500px] max-h-[400px] md:max-h-[600px] lg:max-h-[800px]'
                      } object-contain bg-black rounded-lg`}
                      controls
                      preload="metadata"
                      playsInline
                      onLoadStart={() => {
                        console.log('🎬 Video load started');
                        console.log('📹 Video URLs:', {
                          videoStreamUrl,
                          secureCreativeUrl,
                          creativeUrl: currentAnalysis?.creativeUrl,
                          finalUrl: videoStreamUrl || secureCreativeUrl || currentAnalysis?.creativeUrl
                        });
                        setVideoLoading(true);
                        setVideoError(null);
                      }}
                      onCanPlay={() => {
                        console.log('✅ Video can play');
                        setVideoLoading(false);
                      }}
                      onTimeUpdate={(e) => setCurrentVideoTime((e.target as HTMLVideoElement).currentTime)}
                      onLoadedMetadata={(e) => {
                        const video = e.target as HTMLVideoElement;
                        const aspectRatio = video.videoWidth / video.videoHeight;
                        console.log('📊 Video metadata loaded:', {
                          width: video.videoWidth,
                          height: video.videoHeight,
                          duration: video.duration
                        });
                        setVideoDuration(video.duration);
                        setVideoMetadata({
                          width: video.videoWidth,
                          height: video.videoHeight,
                          aspectRatio
                        });
                        setVideoLoading(false);
                      }}
                      onError={(e) => {
                        const video = e.target as HTMLVideoElement;
                        console.error('❌ Video loading error:', e);
                        console.error('📹 Video element ACTUAL src at error:', video.src);
                        console.error('📹 Video element src attribute at error:', video.getAttribute('src'));
                        console.error('📹 React state URLs:', {
                          videoStreamUrl,
                          secureCreativeUrl,
                          creativeUrl: currentAnalysis?.creativeUrl,
                          finalUrl: videoStreamUrl || secureCreativeUrl || currentAnalysis?.creativeUrl
                        });
                        console.error('📹 Video error details:', {
                          code: video.error?.code,
                          message: video.error?.message
                        });
                        setVideoLoading(false);
                        setVideoError('Failed to load video. Click retry to try again.');
                        handleVideoLoadError();
                      }}
                      style={{
                        aspectRatio: videoMetadata.aspectRatio ? `${videoMetadata.aspectRatio}` : undefined
                      }}
                    />
                  )}

                  {/* Video Metadata Display */}
                  {videoMetadata.width && videoMetadata.height && (
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {videoMetadata.width} × {videoMetadata.height}
                    </div>
                  )}

                  {/* Fullscreen Toggle Button */}
                  <button
                    onClick={toggleFullscreen}
                    className="absolute top-2 right-2 bg-black/70 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                    title={isVideoFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  >
                    <Maximize className="w-4 h-4" />
                  </button>
                  
                  {/* Enhanced Video Timeline with Engagement Heatmap and All Markers */}
                  <div className="absolute bottom-12 left-2 right-2">
                    <div className="relative h-3 bg-black/20 rounded-full overflow-hidden">
                      {/* Engagement Heatmap Background */}
                      {getEngagementHeatmap().map((segment: any, index: number) => (
                        <div
                          key={`heatmap-${index}`}
                          className="absolute top-0 h-full transition-all duration-300"
                          style={{
                            left: `${segment.position}%`,
                            width: `${100 / getEngagementHeatmap().length}%`,
                            backgroundColor: `rgba(139, 92, 246, ${segment.intensity * 0.4})`,
                          }}
                          title={`Engagement: ${Math.round(segment.intensity * 100)}% at ${formatTime(segment.timestamp)}`}
                        />
                      ))}
                      
                      {/* Video Progress Bar */}
                      <div
                        className="absolute h-full bg-white rounded-full transition-all duration-100 shadow-sm"
                        style={{ width: `${(currentVideoTime / videoDuration) * 100}%` }}
                      />
                      
                      {/* All Timeline Markers */}
                      {getTimelineMarkers().map((marker: any, index: number) => {
                        const markerColor = 
                          marker.type === 'issue' ? (
                            marker.severity === 'critical' || marker.severity === 'high' ? 'bg-red-500 border-red-600' :
                            marker.severity === 'medium' ? 'bg-yellow-500 border-yellow-600' : 
                            'bg-blue-500 border-blue-600'
                          ) : marker.type === 'moment' ? 'bg-purple-500 border-purple-600' :
                          'bg-green-500 border-green-600';
                        
                        const markerSize = marker.type === 'issue' && (marker.severity === 'critical' || marker.severity === 'high') ? 'w-3 h-3' : 'w-2.5 h-2.5';
                        
                        return (
                          <div
                            key={`marker-${index}`}
                            className={`absolute top-0 ${markerSize} rounded-full cursor-pointer transform -translate-x-1/2 border-2 transition-all duration-200 hover:scale-125 ${markerColor}`}
                            style={{ left: `${(marker.timestamp / videoDuration) * 100}%` }}
                            onClick={() => handleTimestampClick(marker.timestamp)}
                            title={`${marker.type.toUpperCase()}: ${marker.title} at ${formatTime(marker.timestamp)}`}
                          />
                        );
                      })}
                    </div>
                    
                    {/* Timeline Legend */}
                    <div className="flex items-center justify-between mt-2 text-xs text-white/80">
                      <span>0:00</span>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span>Issues</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <span>Key Moments</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span>Recommendations</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-purple-300 rounded-full"></div>
                          <span>Engagement</span>
                        </div>
                      </div>
                      <span>{formatTime(videoDuration)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative group">
                  <img
                    src={showEnhancedImage && enhancedImageUrl ? (secureEnhancedUrl || enhancedImageUrl) : (secureCreativeUrl || currentAnalysis.creativeUrl)}
                    alt={currentAnalysis.creativeName}
                    className="w-full h-48 sm:h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setShowImagePreview(true)}
                  />
                  {/* Enhanced versions selector */}
                  {(enhancedVersions.length > 0 || enhancedImageUrl) && (
                    <div className="absolute top-2 right-2 flex items-center space-x-2 z-20">
                      {/* Version selector dropdown */}
                      {enhancedVersions.length > 0 && (
                        <select
                          value={showEnhancedImage ? selectedEnhancedVersion : -1}
                          onChange={(e) => {
                            const index = parseInt(e.target.value);
                            if (index === -1) {
                              setShowEnhancedImage(false);
                            } else if (enhancedVersions[index]) {
                              setSelectedEnhancedVersion(index);
                              setEnhancedImageUrl(enhancedVersions[index].enhanced_image_url);
                              setShowEnhancedImage(true);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-sm font-medium text-gray-700 hover:bg-white transition-colors shadow-md border-0 cursor-pointer"
                        >
                          <option value={-1}>Original</option>
                          {enhancedVersions.map((version, index) => (
                            <option key={version.id} value={index}>
                              Enhanced #{enhancedVersions.length - index}
                            </option>
                          ))}
                        </select>
                      )}
                      
                      {/* Legacy toggle button for single enhanced image */}
                      {enhancedVersions.length === 0 && enhancedImageUrl && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEnhancedImage(!showEnhancedImage);
                          }}
                          className="px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg flex items-center space-x-2 text-sm font-medium text-gray-700 hover:bg-white transition-colors shadow-md"
                        >
                          <Sparkles className="w-4 h-4" />
                          <span>{showEnhancedImage ? 'Show Original' : 'Show Enhanced'}</span>
                        </button>
                      )}
                    </div>
                  )}
                  {/* Click to view overlay - positioned above heatmap */}
                  <div 
                    className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
                    onClick={() => setShowImagePreview(true)}
                  >
                    <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center space-x-2 text-gray-800 pointer-events-none">
                      <Eye className="w-4 h-4" />
                      <span className="text-sm font-medium">Click to view full size</span>
                    </div>
                  </div>
                  {/* Attention Heatmap Overlay for Images */}
                  {currentAnalysis.performanceAnalysis?.attentionHeatmap && (
                    <div className="absolute inset-0 pointer-events-none">
                      {(currentAnalysis.performanceAnalysis.attentionHeatmap.hotspots || []).map((hotspot: any, index: number) => (
                        <div
                          key={index}
                          className="absolute w-4 h-4 rounded-full border-2 border-red-500 bg-red-500/30 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                          style={{
                            left: `${hotspot.x}%`,
                            top: `${hotspot.y}%`,
                            opacity: hotspot.intensity / 100
                          }}
                          title={`${hotspot.element}: ${hotspot.intensity}% attention`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Overall Scores - make them conditional for videos to save space */}
            {currentAnalysis.creativeType !== 'video' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{currentAnalysis.overallScore}%</div>
                  <div className="text-gray-600 text-sm">Compliance Score</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${currentAnalysis.overallScore}%` }}
                    ></div>
                  </div>
                </div>
                
                {currentAnalysis.performanceAnalysis && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-1">
                      {currentAnalysis.performanceAnalysis.overallPerformanceScore}%
                    </div>
                    <div className="text-gray-600 text-sm">Performance Score</div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${currentAnalysis.performanceAnalysis.overallPerformanceScore}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="space-y-2">
              {/* Show Enhance button for images, Duplicate for videos */}
              {currentAnalysis.creativeType === 'image' ? (
                <button 
                  onClick={() => setShowEnhancementModal(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-sm sm:text-base"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Enhance with AI</span>
                </button>
              ) : (
                <button className="w-full flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-sm sm:text-base">
                  <Copy className="w-4 h-4" />
                  <span>Duplicate</span>
                </button>
              )}
              
              {/* Re-analyze button (secondary action) */}
              <button 
                onClick={handleReanalyze}
                disabled={isReanalyzing || !currentCreativeId || (!realAnalysisData && !!currentAnalysisId && !!mockAnalysisData[currentAnalysisId as keyof typeof mockAnalysisData])}
                className="w-full flex items-center justify-center gap-2 p-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {isReanalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Re-analyzing...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    <span>
                      {(!realAnalysisData && currentAnalysisId && mockAnalysisData[currentAnalysisId as keyof typeof mockAnalysisData]) 
                        ? 'Re-analyze (Demo Only)' 
                        : 'Re-analyze'
                      }
                    </span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>

        {/* Analysis Results */}
        <div className={`space-y-4 sm:space-y-6 lg:space-y-8 ${currentAnalysis.creativeType === 'video' ? 'col-span-1 mt-4 sm:mt-6 lg:mt-8' : 'col-span-1 lg:col-span-2'}`}>
          {/* Video-specific overall scores for better layout */}
          {currentAnalysis.creativeType === 'video' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
            >
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Overall Scores</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1">{currentAnalysis.overallScore}%</div>
                  <div className="text-gray-600 text-sm">Compliance Score</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${currentAnalysis.overallScore}%` }}
                    ></div>
                  </div>
                </div>
                
                {currentAnalysis.performanceAnalysis && (
                  <div className="text-center">
                    <div className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-600 mb-1">
                      {currentAnalysis.performanceAnalysis.overallPerformanceScore}%
                    </div>
                    <div className="text-gray-600 text-sm">Performance Score</div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${currentAnalysis.performanceAnalysis.overallPerformanceScore}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Video Duration */}
                <div className="text-center">
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 mb-1">{formatTime(currentAnalysis.videoDuration || videoDuration || 0)}</div>
                  <div className="text-gray-600 text-sm">Duration</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500 w-full"></div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 mb-4 sm:mb-6">
              <button
                onClick={() => setActiveTab('compliance')}
                className={`flex items-center gap-2 px-3 py-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-all w-full sm:w-auto justify-center sm:justify-start ${
                  activeTab === 'compliance'
                    ? 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>Compliance Analysis</span>
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`flex items-center gap-2 px-3 py-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-all w-full sm:w-auto justify-center sm:justify-start ${
                  activeTab === 'performance'
                    ? 'bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border border-purple-200'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Performance Analysis</span>
              </button>
            </div>

            {/* Compliance Tab */}
            {activeTab === 'compliance' && (
              <div className="space-y-6">
                {/* Platform Scores */}
                <div>
                  <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Platform Compliance</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {Object.entries(currentAnalysis.platforms).map(([platform, data]: [string, any]) => {
                      const StatusIcon = getStatusIcon(data.status);
                      const platformData = getPlatformData(platform);
                      
                      const isSelected = selectedPlatform === platform;
                      
                      return (
                        <div 
                          key={platform} 
                          className={`border rounded-lg p-3 sm:p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                            isSelected 
                              ? 'border-purple-500 bg-purple-50 shadow-md' 
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          onClick={() => {
                            setSelectedPlatform(selectedPlatform === platform ? null : platform);
                          }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <PlatformIcon platform={platform} size="lg" />
                              <span className="font-medium text-gray-900">{platformData.name}</span>
                              {isSelected && (
                                <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded-full">
                                  Filtered
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getScoreColor(data.score).status}`}>
                                <StatusIcon className="w-3 h-3 inline mr-1" />
                                {formatStatus(data.status)}
                              </span>
                              <span className="text-base sm:text-lg font-bold text-gray-900">{data.score}%</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${getScoreColor(data.score).bar}`}
                              style={{ width: `${data.score}%` }}
                            ></div>
                          </div>
                          {data.issues && data.issues.length > 0 && (
                            <div className="text-xs text-gray-600">
                              {data.issues.length} issue{data.issues.length !== 1 ? 's' : ''} detected
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Detected Issues */}
                <div>
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Detected Issues</h2>
                    {selectedPlatform && (
                      <button
                        onClick={() => setSelectedPlatform(null)}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                        <span>Clear Filter</span>
                      </button>
                    )}
                  </div>
                  
                  {selectedPlatform && (
                    <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Info className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-purple-700">
                          Showing issues for: <strong>{getPlatformData(selectedPlatform).name}</strong>
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {(() => {
                    const filteredIssues = selectedPlatform 
                      ? currentAnalysis.detectedIssues.filter((issue: any) => issue.platform === selectedPlatform)
                      : currentAnalysis.detectedIssues;
                    
                    if (filteredIssues.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {selectedPlatform 
                              ? `No Issues for ${getPlatformData(selectedPlatform).name}` 
                              : 'No Issues Detected'
                            }
                          </h3>
                          <p className="text-gray-600">
                            {selectedPlatform 
                              ? `Your creative complies with ${getPlatformData(selectedPlatform).name} guidelines.`
                              : 'Your creative complies with all platform guidelines.'
                            }
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="space-y-4">
                        {filteredIssues.map((issue: any) => {
                        const platformData = getPlatformData(issue.platform);
                        
                        return (
                          <div key={issue.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-start space-x-3">
                                <PlatformIcon platform={issue.platform} size="lg" />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h3 className="font-semibold text-gray-900">{issue.title}</h3>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(issue.severity)}`}>
                                      {issue.severity}
                                    </span>
                                    {issue.timestamp && (
                                      <button
                                        onClick={() => handleTimestampClick(issue.timestamp!)}
                                        className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded hover:bg-purple-200 transition-colors flex items-center space-x-1"
                                      >
                                        <Play className="w-3 h-3" />
                                        <span>{issue.timeDisplay}</span>
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-gray-700 mb-2">{issue.description}</p>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <span className="font-medium">{platformData.name}</span>
                                    {issue.location && <span>Location: {issue.location}</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <BookOpen className="w-4 h-4 text-gray-600" />
                                <span className="font-medium text-gray-900">Platform Rule</span>
                              </div>
                              <p className="text-sm text-gray-700 mb-2">{issue.rule}</p>
                              <a
                                href={issue.ruleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700"
                              >
                                <span>View official documentation</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                            
                            <div className="bg-blue-50 rounded-lg p-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <Lightbulb className="w-4 h-4 text-blue-600" />
                                <span className="font-medium text-blue-900">Recommendation</span>
                              </div>
                              <p className="text-sm text-blue-800">{issue.recommendation}</p>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && currentAnalysis.performanceAnalysis && (
              <div className="space-y-6">
                {/* Performance Overview */}
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Performance Potential</h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 sm:p-6 border border-purple-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Overall Performance</h3>
                          <p className="text-sm text-gray-600">Predicted ad effectiveness</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <div className="text-2xl sm:text-3xl font-bold text-purple-600">
                          {currentAnalysis.performanceAnalysis.overallPerformanceScore}%
                        </div>
                        <div className={`text-base sm:text-lg font-bold ${getPerformanceGrade(currentAnalysis.performanceAnalysis.overallPerformanceScore).color}`}>
                          {getPerformanceGrade(currentAnalysis.performanceAnalysis.overallPerformanceScore).grade}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 sm:p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                          <Brain className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Brand Awareness</h3>
                          <p className="text-sm text-gray-600">Memorability score</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                          {currentAnalysis.performanceAnalysis.brandAwarenessScore}%
                        </div>
                        <div className={`text-base sm:text-lg font-bold ${getPerformanceGrade(currentAnalysis.performanceAnalysis.brandAwarenessScore).color}`}>
                          {getPerformanceGrade(currentAnalysis.performanceAnalysis.brandAwarenessScore).grade}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platform Performance */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Platform Performance Predictions</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {Object.entries(currentAnalysis.platforms || {}).map(([platform, data]: [string, any]) => {
                      const platformData = getPlatformData(platform);
                      
                      return (
                        <div key={platform} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <PlatformIcon platform={platform} size="lg" />
                              <span className="font-medium text-gray-900">{platformData?.name || platform}</span>
                            </div>
                            <div className="text-base sm:text-lg font-bold text-purple-600">{data.score}%</div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
                            <div>
                              <span className="text-gray-600">Predicted CTR</span>
                              <div className="font-semibold text-gray-900">{formatPercentage(data.predictedCTR)}%</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Engagement</span>
                              <div className="font-semibold text-gray-900">{formatPercentage(data.predictedEngagement)}%</div>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-600">Algorithm Favorability</span>
                              <div className="font-semibold text-gray-900">{data.algorithmFavorability || 0}/100</div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <span className="text-xs font-medium text-green-700">Strengths:</span>
                              <ul className="text-xs text-gray-600 ml-2">
                                {(data.strengths || ['No specific strengths identified']).map((strength: string, index: number) => (
                                  <li key={index}>• {strength}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-red-700">Areas for improvement:</span>
                              <ul className="text-xs text-gray-600 ml-2">
                                {(data.weaknesses || data.improvements || ['No specific improvements identified']).map((weakness: any, index: number) => (
                                  <li key={index}>• {weakness}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Key Performance Factors */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Key Performance Factors</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {Object.entries(currentAnalysis.performanceAnalysis?.keyFactors || {}).map(([factor, data]: [string, any]) => {
                      const icons = {
                        attention: MousePointer,
                        clarity: Eye,
                        engagement: Heart,
                        branding: Award
                      };
                      const Icon = icons[factor as keyof typeof icons] || Target;
                      
                      return (
                        <div key={factor} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-gray-900 capitalize">{factor}</h4>
                                <span className="text-base sm:text-lg font-bold text-purple-600">{data.score}%</span>
                              </div>
                              <p className="text-sm text-gray-600">{data.description}</p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                            <div
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${data.score}%` }}
                            ></div>
                          </div>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {(data.details || []).map((detail: any, index: number) => (
                              <li key={index} className="flex items-center space-x-2">
                                <div className="w-1 h-1 bg-purple-400 rounded-full"></div>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Visual Creative Analysis */}
                {currentAnalysis.performanceAnalysis?.visualAnalysis && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Visual Creative Analysis</h3>
                    
                    {/* Visual Scores Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                      {[
                        { key: 'visualScore', label: 'Visual Score', icon: Eye, color: 'from-blue-500 to-cyan-500' },
                        { key: 'colorScore', label: 'Color Scheme', icon: Palette, color: 'from-purple-500 to-pink-500' },
                        { key: 'layoutScore', label: 'Layout', icon: Layout, color: 'from-green-500 to-emerald-500' },
                        { key: 'typographyScore', label: 'Typography', icon: Type, color: 'from-orange-500 to-red-500' }
                      ].map(({ key, label, icon: Icon, color }) => (
                        <div key={key} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className={`w-10 h-10 bg-gradient-to-r ${color} rounded-lg flex items-center justify-center`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{label}</h4>
                              <span className="text-2xl font-bold text-gray-800">
                                {currentAnalysis.performanceAnalysis.visualAnalysis[key as keyof typeof currentAnalysis.performanceAnalysis.visualAnalysis]}%
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`bg-gradient-to-r ${color} h-2 rounded-full transition-all duration-500`}
                              style={{ width: `${currentAnalysis.performanceAnalysis.visualAnalysis[key as keyof typeof currentAnalysis.performanceAnalysis.visualAnalysis]}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Audience Alignment */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Target Audience Alignment</h4>
                          <p className="text-sm text-gray-600">How well this creative matches your target demographic</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <div className="text-2xl sm:text-3xl font-bold text-indigo-600">
                          {currentAnalysis.performanceAnalysis.visualAnalysis.audienceAlignment}%
                        </div>
                        <div className="flex-1">
                          <div className="w-full bg-white bg-opacity-50 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${currentAnalysis.performanceAnalysis.visualAnalysis.audienceAlignment}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Design Insights */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                          <Lightbulb className="w-5 h-5 text-yellow-500" />
                          <span>Design Insights</span>
                        </h4>
                        <div className="space-y-3">
                          {currentAnalysis.performanceAnalysis.visualAnalysis.designInsights?.map((insight: string, index: number) => (
                            <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                                <p className="text-sm text-gray-700">{insight}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                          <Target className="w-5 h-5 text-blue-500" />
                          <span>Visual Recommendations</span>
                        </h4>
                        <div className="space-y-3">
                          {currentAnalysis.performanceAnalysis.visualAnalysis.visualRecommendations?.map((recommendation: string, index: number) => (
                            <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                <p className="text-sm text-gray-700">{recommendation}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Performance Recommendations */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">AI Performance Recommendations</h3>
                  <div className="space-y-4">
                    {(currentAnalysis.aiRecommendations || []).map((recommendation: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="font-semibold text-gray-900">{recommendation.title}</h4>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(recommendation.priority)}`}>
                                  {recommendation.priority} priority
                                </span>
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                                  {recommendation.category}
                                </span>
                              </div>
                              <p className="text-gray-700 mb-2">{recommendation.description}</p>
                              <div className="flex items-center space-x-4 text-sm">
                                <span className="text-green-600 font-medium">{recommendation.impact}</span>
                                <span className="text-gray-500">
                                  Platforms: {Array.isArray(recommendation.platforms) ? 
                                    (recommendation.platforms.includes('all') ? 'All' : recommendation.platforms.join(', ')) : 
                                    recommendation.platforms}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Video Timeline Analysis (for videos) */}
                {currentAnalysis.creativeType === 'video' && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Video Timeline Analysis</h3>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {/* All Timeline Markers */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">Timeline Markers</h4>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {getTimelineMarkers().map((marker: any, index: number) => {
                              const markerTypeColor = 
                                marker.type === 'issue' ? 'bg-red-50 border-red-200 text-red-800' :
                                marker.type === 'moment' ? 'bg-purple-50 border-purple-200 text-purple-800' :
                                'bg-green-50 border-green-200 text-green-800';
                              
                              const severityIcon = 
                                marker.type === 'issue' && (marker.severity === 'critical' || marker.severity === 'high') ? '🚨' :
                                marker.type === 'issue' && marker.severity === 'medium' ? '⚠️' :
                                marker.type === 'issue' ? 'ℹ️' :
                                marker.type === 'moment' ? '📍' : '💡';
                              
                              return (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg border ${markerTypeColor}`}>
                                  <div className="flex-shrink-0">
                                    <button
                                      onClick={() => handleTimestampClick(marker.timestamp)}
                                      className="flex items-center space-x-2 px-3 py-1 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                                    >
                                      <span>{severityIcon}</span>
                                      <span>{formatTime(marker.timestamp)}</span>
                                    </button>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm">{marker.title}</p>
                                    {marker.description && (
                                      <p className="text-xs mt-1 opacity-80">{marker.description}</p>
                                    )}
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className="text-xs opacity-70 capitalize">{marker.type}</span>
                                      {marker.engagement && (
                                        <span className="text-xs opacity-70">
                                          Engagement: {marker.engagement}%
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {getTimelineMarkers().length === 0 && (
                              <p className="text-gray-500 text-sm italic">No timeline markers found</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Engagement Summary */}
                        {currentAnalysis.timelineAnalysis?.engagementCurve && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-3">Engagement Summary</h4>
                            <div className="space-y-3">
                              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3">
                                <div className="text-sm font-medium text-gray-900 mb-1">Average Engagement</div>
                                <div className="text-xl font-bold text-purple-600">
                                  {Math.round(
                                    currentAnalysis.timelineAnalysis.engagementCurve
                                      .reduce((sum: number, point: any) => sum + point.engagement, 0) / 
                                    currentAnalysis.timelineAnalysis.engagementCurve.length
                                  )}%
                                </div>
                              </div>
                              
                              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3">
                                <div className="text-sm font-medium text-gray-900 mb-1">Peak Engagement</div>
                                <div className="text-xl font-bold text-green-600">
                                  {Math.max(...currentAnalysis.timelineAnalysis.engagementCurve.map((point: any) => point.engagement))}%
                                </div>
                              </div>
                              
                              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3">
                                <div className="text-sm font-medium text-gray-900 mb-1">Drop-off Points</div>
                                <div className="text-xl font-bold text-orange-600">
                                  {currentAnalysis.timelineAnalysis.keyMoments.filter((moment: any) => moment.attentionLevel < 50).length}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* AI Recommendations (Compliance) */}
          {activeTab === 'compliance' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
            >
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">AI Recommendations</h2>
              <div className="space-y-3">
                {(currentAnalysis.aiRecommendations || []).map((recommendation: any, index: number) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                    <Zap className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-gray-900">{recommendation.title}</h4>
                        {recommendation.timestamp !== undefined && currentAnalysis.creativeType === 'video' && (
                          <button
                            onClick={() => handleTimestampClick(recommendation.timestamp!)}
                            className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded hover:bg-purple-200 transition-colors flex items-center space-x-1"
                          >
                            <Play className="w-3 h-3" />
                            <span>{formatTime(recommendation.timestamp!)}</span>
                          </button>
                        )}
                      </div>
                      <p className="text-gray-700 text-sm">{recommendation.description}</p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-green-600 font-medium">{recommendation.impact}</span>
                        <span className="text-gray-500">
                          Platforms: {Array.isArray(recommendation.platforms) ? 
                            (recommendation.platforms.includes('all') ? 'All' : recommendation.platforms.join(', ')) : 
                            recommendation.platforms}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(recommendation.priority)}`}>
                          {recommendation.priority.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Recent Analyses Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <RecentAnalysesWidget 
              onViewAll={() => navigate('/dashboard/recent-analyses')}
              onHistoryClick={(analysis) => {
                if (analysis.id) {
                  handleRecentAnalysisClick(analysis.id);
                }
              }}
            />
          </motion.div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {/* New Version Modal */}
        {showNewVersionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 w-full max-w-md mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Create New Version</h3>
                <button
                  onClick={() => setShowNewVersionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Version Description
                  </label>
                  <input
                    type="text"
                    value={versionDescription}
                    onChange={(e) => setVersionDescription(e.target.value)}
                    placeholder="e.g., Updated headline text, Changed background image"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload New Creative
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => setVersionFile(e.target.files?.[0] || null)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div 
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isVersionDragging 
                          ? 'border-purple-400 bg-purple-50' 
                          : 'border-gray-300 hover:border-purple-400'
                      }`}
                      onDragEnter={handleVersionDragEnter}
                      onDragLeave={handleVersionDragLeave}
                      onDragOver={handleVersionDragOver}
                      onDrop={handleVersionDrop}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      {versionFile ? (
                        <p className="text-sm text-gray-900 font-medium">{versionFile.name}</p>
                      ) : (
                        <>
                          <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                          <p className="text-xs text-gray-500 mt-1">Images (JPG, PNG, GIF, WebP, HEIC, HEIF) up to 20MB, Videos (MP4, MOV, AVI, WebM, etc.) up to 1GB</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowNewVersionModal(false);
                    setVersionDescription('');
                    setVersionFile(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={creatingVersion}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateVersion}
                  disabled={!versionFile || creatingVersion}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingVersion ? 'Creating...' : 'Create & Analyze'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add to Project Modal */}
        {showProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 w-full max-w-md mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Add to Project</h3>
                <button
                  onClick={handleCloseProjectModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Project
                  </label>
                  {loadingProjects ? (
                    <div className="flex items-center justify-center py-3 border border-gray-300 rounded-lg">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-600 mr-2" />
                      <span className="text-gray-600">Loading projects...</span>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto border border-gray-300 rounded-lg">
                      {projects.length === 0 ? (
                        <div className="p-4 text-center text-gray-600">
                          <span>No projects found. Create your first project to get started.</span>
                        </div>
                      ) : (
                        projects.map((project) => (
                          <div
                            key={project.id}
                            className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                              selectedProjectId === project.id ? 'bg-purple-50 border-purple-200' : ''
                            }`}
                            onClick={() => setSelectedProjectId(project.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="radio"
                                    name="project"
                                    checked={selectedProjectId === project.id}
                                    onChange={() => setSelectedProjectId(project.id)}
                                    className="w-4 h-4 text-purple-600"
                                  />
                                  <h4 className="font-medium text-gray-900 truncate">{project.name}</h4>
                                  {(project as any).hasExistingCreative && (
                                    <div className="flex items-center space-x-1">
                                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                      <span className="text-xs text-yellow-600 font-medium">Already exists</span>
                                    </div>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {project.recentActivity?.creativesCount || 0} creatives • {project.memberCount} member{project.memberCount !== 1 ? 's' : ''}
                                  {(project as any).hasExistingCreative && (project as any).existingCreativeInfo && (
                                    <div className="text-xs text-yellow-600 mt-1">
                                      Duplicate of "{(project as any).existingCreativeInfo.name}" added {new Date((project as any).existingCreativeInfo.created_at).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {(project as any).hasExistingCreative && (
                                <div className="ml-2">
                                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <AlertTriangle className="w-3 h-3 text-yellow-600" />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                      
                      {/* Create New Project Option */}
                      <div
                        className={`p-3 cursor-pointer border-t border-gray-200 hover:bg-gray-50 transition-colors ${
                          selectedProjectId === 'new' ? 'bg-purple-50 border-purple-200' : ''
                        }`}
                        onClick={() => setSelectedProjectId('new')}
                      >
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="project"
                            checked={selectedProjectId === 'new'}
                            onChange={() => setSelectedProjectId('new')}
                            className="w-4 h-4 text-purple-600"
                          />
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                              <FolderPlus className="w-3 h-3 text-purple-600" />
                            </div>
                            <span className="font-medium text-purple-600">Create New Project</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {projectError && (
                    <p className="text-red-600 text-sm mt-2 flex items-center space-x-1">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{projectError}</span>
                    </p>
                  )}
                  
                  {/* Duplicate Warning */}
                  {selectedProjectId && selectedProjectId !== 'new' && projects.find(p => p.id === selectedProjectId && (p as any).hasExistingCreative) && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-yellow-800">Duplicate Creative Detected</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            This creative already exists in the selected project. Adding it again will create a duplicate entry.
                          </p>
                          <div className="mt-2 text-xs text-yellow-600">
                            Existing creative: "{((projects.find(p => p.id === selectedProjectId) as any)?.existingCreativeInfo?.name || 'Unknown')}"
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Add as
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="addType" value="new-creative" defaultChecked className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-700">New creative in project</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="addType" value="new-version" className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-700">New version of existing creative</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCloseProjectModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddToProject}
                  disabled={addingToProject || !selectedProjectId || selectedProjectId === 'new'}
                  className={`flex-1 px-4 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 ${
                    selectedProjectId && selectedProjectId !== 'new' && projects.find(p => p.id === selectedProjectId && (p as any).hasExistingCreative)
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600'
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                  }`}
                >
                  {addingToProject ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      {selectedProjectId && selectedProjectId !== 'new' && projects.find(p => p.id === selectedProjectId && (p as any).hasExistingCreative) ? (
                        <>
                          <AlertTriangle className="w-4 h-4" />
                          <span>Add Duplicate</span>
                        </>
                      ) : (
                        <span>Add to Project</span>
                      )}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Version Comparison Modal */}
      {showComparison && comparison && (
        <EnhancedVersionComparison 
          comparison={comparison} 
          onClose={() => setShowComparison(false)} 
        />
      )}

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {showRestoreConfirmModal && versionToRestore && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg sm:rounded-xl shadow-xl p-4 sm:p-6 w-full max-w-md mx-4"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Restore Version</h3>
                  <p className="text-sm text-gray-600">Make this version the current one?</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{versionToRestore.version}</div>
                    <div className="text-sm text-gray-600">{versionToRestore.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-base sm:text-lg font-bold text-gray-900">{versionToRestore.score}%</div>
                    <div className="text-xs text-gray-500">
                      {new Date(versionToRestore.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">You can switch between versions anytime</p>
                    <p>All versions remain available for future restoration.</p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowRestoreConfirmModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeRestore}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Restore Version
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Share Analysis Modal */}
      <AnimatePresence>
        {showShareModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowShareModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 relative z-10"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Share Analysis</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Copy Options */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Share Link</h4>
                  <div className="space-y-2">
                    {/* Copy Link Only */}
                    <button
                      onClick={() => {
                        const shareUrl = `${window.location.origin}/dashboard/analysis?id=${currentAnalysisId}`;
                        navigator.clipboard.writeText(shareUrl);
                        showShareNotification('Analysis link copied to clipboard!');
                      }}
                      className="w-full flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      <span>Copy Link</span>
                    </button>
                    
                    {/* Copy with Context */}
                    <button
                      onClick={() => {
                        const urls = getSocialShareUrls(currentAnalysisId);
                        const shareUrl = `${window.location.origin}/dashboard/analysis?id=${currentAnalysisId}`;
                        const creativeName = currentAnalysis?.creativeName || 'Creative';
                        const score = currentAnalysis?.overallScore || 0;
                        const creativeType = currentAnalysis?.creativeType || 'creative';
                        
                        // Generate the same contextual message logic
                        let totalIssues = 0;
                        let criticalIssues = 0;
                        let platformCount = 0;
                        
                        if (currentAnalysis?.platforms) {
                          platformCount = Object.keys(currentAnalysis.platforms).length;
                          Object.values(currentAnalysis.platforms).forEach((platform: any) => {
                            if (platform.issues) {
                              totalIssues += platform.issues.length;
                              criticalIssues += platform.issues.filter((issue: any) => issue.severity === 'critical').length;
                            }
                          });
                        }
                        
                        let contextMessage = '';
                        if (score >= 90) {
                          contextMessage = `Excellent compliance! My ${creativeType} "${creativeName}" scored ${score}% across ${platformCount} platforms with minimal issues.`;
                        } else if (score >= 80) {
                          contextMessage = `Strong performance! "${creativeName}" achieved ${score}% compliance score with ${totalIssues} areas for improvement across ${platformCount} platforms.`;
                        } else if (score >= 70) {
                          contextMessage = `Good potential! "${creativeName}" scored ${score}% with ${totalIssues} optimization opportunities identified across ${platformCount} advertising platforms.`;
                        } else if (score >= 60) {
                          contextMessage = `Work in progress: "${creativeName}" has ${score}% compliance with ${totalIssues} issues to address${criticalIssues > 0 ? ` (${criticalIssues} critical)` : ''} across ${platformCount} platforms.`;
                        } else {
                          contextMessage = `Major improvements needed: "${creativeName}" scored ${score}% with ${totalIssues} compliance issues${criticalIssues > 0 ? ` including ${criticalIssues} critical` : ''} across ${platformCount} platforms.`;
                        }
                        
                        const aiInsight = currentAnalysis?.aiRecommendations?.length > 0 ? 
                          ` AI provided ${currentAnalysis.aiRecommendations.length} actionable recommendations.` : '';
                        
                        const fullMessage = `${contextMessage}${aiInsight}\n\n🤖 Powered by AI creative analysis | Rulevia\n${shareUrl}`;
                        
                        navigator.clipboard.writeText(fullMessage);
                        showShareNotification('Analysis with context copied to clipboard!');
                      }}
                      className="w-full flex items-center justify-center space-x-2 bg-purple-100 text-purple-700 px-4 py-3 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>Copy with Context</span>
                    </button>
                  </div>
                </div>

                {/* Social Media Share */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Share on Social Media</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* LinkedIn */}
                    <button
                      onClick={() => {
                        const urls = getSocialShareUrls(currentAnalysisId);
                        // Auto-copy message to clipboard for LinkedIn
                        navigator.clipboard.writeText(urls.linkedin.message);
                        showShareNotification(urls.linkedin.instructions);
                        setTimeout(() => {
                          window.open(urls.linkedin.url, '_blank', 'width=600,height=400');
                        }, 1000);
                      }}
                      className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <span>LinkedIn</span>
                    </button>
                    
                    {/* X (Twitter) */}
                    <button
                      onClick={() => {
                        const urls = getSocialShareUrls(currentAnalysisId);
                        showShareNotification(urls.twitter.instructions);
                        window.open(urls.twitter.url, '_blank', 'width=600,height=400');
                      }}
                      className="flex items-center justify-center space-x-2 bg-gray-900 text-white px-4 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span>X</span>
                    </button>
                    
                    {/* Facebook */}
                    <button
                      onClick={() => {
                        const urls = getSocialShareUrls(currentAnalysisId);
                        // Auto-copy message to clipboard for Facebook
                        navigator.clipboard.writeText(urls.facebook.message);
                        showShareNotification(urls.facebook.instructions);
                        setTimeout(() => {
                          window.open(urls.facebook.url, '_blank', 'width=600,height=400');
                        }, 1000);
                      }}
                      className="flex items-center justify-center space-x-2 bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      <span>Facebook</span>
                    </button>
                    
                    {/* Reddit */}
                    <button
                      onClick={() => {
                        const urls = getSocialShareUrls(currentAnalysisId);
                        showShareNotification(urls.reddit.instructions);
                        window.open(urls.reddit.url, '_blank', 'width=600,height=400');
                      }}
                      className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-4 py-3 rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                      </svg>
                      <span>Reddit</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Share Notification */}
      <AnimatePresence>
        {shareNotification && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2"
          >
            <CheckCircle className="w-4 h-4" />
            <span>{shareNotification}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Undo Restore Notification */}
      <AnimatePresence>
        {showUndoNotification && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-4 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-3"
          >
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4" />
              <span>Version restored successfully!</span>
            </div>
            <button
              onClick={handleUndoRestore}
              className="ml-4 px-3 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-sm font-medium transition-colors"
            >
              Undo
            </button>
            <button
              onClick={() => {
                setShowUndoNotification(false);
                setRestoringVersion(null);
                if (undoTimeout) {
                  clearTimeout(undoTimeout);
                  setUndoTimeout(null);
                }
                setPreviousCurrentVersion(null);
              }}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhancement Modal */}
      {showEnhancementModal && currentAnalysis && realAnalysisData && (
        <CreativeEnhancementModal
          isOpen={showEnhancementModal}
          onClose={() => setShowEnhancementModal(false)}
          creativeId={currentAnalysis.id}
          creativeUrl={secureCreativeUrl || currentAnalysis.creativeUrl}
          creativeName={currentAnalysis.name}
          analysisData={realAnalysisData}
          analysisId={currentAnalysis.id}
          onEnhancementComplete={async (result) => {
            if (result.success) {
              console.log('Enhancement completed successfully');
              // Reload enhanced versions to show the new one
              await loadEnhancedVersions(currentAnalysis.id);
            }
          }}
        />
      )}

      {/* Image Preview Modal */}
      {currentAnalysis && (
        <ImagePreviewModal
          isOpen={showImagePreview}
          onClose={() => setShowImagePreview(false)}
          imageUrl={showEnhancedImage && enhancedImageUrl ? (secureEnhancedUrl || enhancedImageUrl) : (secureCreativeUrl || currentAnalysis.creativeUrl)}
          imageName={currentAnalysis.creativeName + (showEnhancedImage && enhancedImageUrl ? ' (Enhanced)' : '')}
          imageType={currentAnalysis.creativeType}
        />
      )}
      </div>
    </div>
  );
}